import { SOUND_REGISTRY } from '../data/soundRegistry.js';

// Music Director file map (docs/about music folder.rtf + docs/
// SOUND_BATCH_PLAN.md section 5). Plain <audio> elements, not Howler/Web
// Audio - duckBGM()/setMusicVolume() below predate this and already
// manipulate `this.bgm.volume` as a plain property (tests/bio_plasma_test.mjs
// asserts exactly that against a `{ volume }`-shaped mock), which is what a
// real HTMLAudioElement gives you for free; Howler's Howl.volume() is a
// method, not a property, and would have broken that contract for no
// benefit (music is single long-form tracks, not many concurrent voices).
const MUSIC_BASE = 'assets/music/';
const MAP_MUSIC_FILES = {
  human: 'loop_ambient_01.ogg',
  sul: 'map/map_sul_map_l.mp3',
  swamp: 'map/map_xeno_swamp.mp3',
  voidglass: 'more/sci_fi/space_ship_interior.m4a',
  stratos: 'map/map_hardcore.mp3',
};
// map_dark/map_grim/map_hardcore.mp3 exist under assets/music/map/ too but
// aren't wired to anything - MAP_IDS (js/engine/maps.js) only has 4 maps
// today; these 3 are reserved for whenever a 5th/6th map ships.
const SHORT_LOOP_FILES = [
  'short/loop_brasssynth.mp3',
  'short/loop_chillgroove.mp3',
  'short/loop_deep_voice_pitch.mp3',
  'short/loop_monumental_alert.mp3',
  'short/loop_moody.mp3',
];
const OST_ROTATION_FILES = ['OST/cargo.mp3', 'OST/score.mp3'];
const OST_ONSTART_FILE = 'OST/onstart_OST.mp3';
const DEMO_LOOP_FILE = 'short/loop_monumental_alert.mp3';
const MUSIC_LAST_DAY_KEY = 'ucg_music_last_day';

// Batch 2 (Kampf/Waffen) - Sound-Review-Ergebnis, docs/SOUND_BATCH_PLAN.md
// section 4.2. Dateien liegen unter assets/sound/ (SFX-Schicht, Howler, see
// section 3.3), nicht unter assets/music/. `explosion_massive`/`tesla`/
// `rocket_launch` sind hier bewusst NICHT drin - Runde 1 der Kandidaten
// wurde abgelehnt (needs_alt), die betroffenen play*()-Methoden bleiben bis
// zur zweiten Runde synthetisch.
const LASER_NORMAL_FILES = ['laser1.ogg', 'laser2.ogg', 'laser3.ogg'];
const LASER_HEAVY_FILES = ['laserLarge_000.ogg', 'laserLarge_001.ogg', 'laserLarge_002.ogg'];
const EXPLOSION_FILES = {
  small: 'explosion_02.ogg',
  medium: 'explosionCrunch_003.ogg',
  large: 'explosionCrunch_004.ogg',
  heavy: 'lowFrequency_explosion_001.ogg',
};

// Batch 5 (docs/SOUND_BATCH_PLAN.md section 4.5) - playBark(race, type)
// lookup. Only the two active races (js/data/cards.js) ever get entries;
// combos not yet approved in the review tool are simply absent, so
// playBark() falls through to its original synthesis for them.
const BARK_FILES = {
  Human: {
    select: 'click2.ogg',
    spawn: { file: 'more/weapons/sword_1.m4a', volume: 0.2 },
    death: 'impactSoft_heavy_002.ogg',
  },
  Sul: {
    select: 'select_001.ogg',
    spawn: 'baked/sul_spawn.m4a',
    attack: 'more/animals/leopard_snarl.m4a',
  },
};

// Unit-specific spawn overrides. Units not listed here fall back to
// BARK_FILES[race].spawn.
export const UNIT_SPAWN_FILES = {
  TeslaWalker: { file: 'more/weapons/sword_2.m4a', volume: 0.2 },
  DevilPatrol: { file: 'even more sounds/Voices_Sound_Effect_Library/Mp3/Human/Human_Evil_02.mp3', volume: 0.8 },
  Orchestra: { file: 'even more sounds/qubodup-rts_warsounds_v2/artil/artil.ready.m4a', volume: 0.8 },
  PinkBeret: { file: 'more/people/crowd_applause_and_cheer_1.m4a', volume: 0.8 },
};

export const CATEGORY_VOICE_CAPS = {
  combat_infantry: 3,
  combat_heavy: 2,
  barks: 2,
  ui: 2,
  card_flip: 6,
  special: 2,
  default: 4,
};

// Loops a single audio file seamlessly by crossfading a second play of the
// same Howl back to its own start just before the current one ends, instead
// of relying on `Howl({ loop: true })`'s hard restart. Most files under
// assets/sound/more/ambience/ were never edited to have a sample-accurate
// loop point (and compressed codecs add their own encoder padding), so a
// plain loop restart produces an audible click or gap - overlapping the tail
// with the next head and cross-fading the volume masks that seam. Mirrors
// the subset of the Howl instance API the ambience call sites use
// (play/pause/stop/playing/volume/rate) so it's a drop-in replacement for
// `new Howl({ loop: true, ... })`.
class CrossfadeLoop {
  constructor(src, { volume = 1, rate = 1, fadeMs = 1500 } = {}) {
    this.src = [src];
    this._fadeMs = fadeMs;
    this._vol = volume;
    this._rate = rate;
    this._wantsPlaying = false;
    this._activeId = null;
    this._crossfadeTimer = null;
    this._stopTimer = null;
    this._dead = false;
    this._howl = new window.Howl({ src: [src], loop: false, volume: 0 });
    this._howl.once('load', () => {
      if (!this._dead && this._wantsPlaying && this._activeId === null) this._begin();
    });
  }

  _begin() {
    this._activeId = this._howl.play();
    this._howl.volume(this._vol, this._activeId);
    try { this._howl.rate(this._rate, this._activeId); } catch {}
    this._scheduleCrossfade();
  }

  _scheduleCrossfade() {
    clearTimeout(this._crossfadeTimer);
    if (this._activeId === null) return;
    const dur = this._howl.duration(this._activeId) || this._howl.duration() || 0;
    const rate = this._rate || 1;
    const playbackSec = dur / rate;
    const fadeSec = this._fadeMs / 1000;
    let elapsed = 0;
    try { elapsed = this._howl.seek(this._activeId) || 0; } catch {}
    const remaining = Math.max(0, playbackSec - elapsed);
    const delayMs = Math.max(0, remaining - fadeSec) * 1000;
    this._crossfadeTimer = setTimeout(() => this._crossfade(), delayMs);
  }

  _crossfade() {
    if (!this._wantsPlaying) return;
    const outgoingId = this._activeId;
    const incomingId = this._howl.play();
    this._howl.volume(0, incomingId);
    try { this._howl.rate(this._rate, incomingId); } catch {}
    this._activeId = incomingId;
    try { this._howl.fade(0, this._vol, this._fadeMs, incomingId); } catch {}
    try { this._howl.fade(this._vol, 0, this._fadeMs, outgoingId); } catch {}
    clearTimeout(this._stopTimer);
    this._stopTimer = setTimeout(() => {
      try { this._howl.stop(outgoingId); } catch {}
    }, this._fadeMs + 100);
    this._scheduleCrossfade();
  }

  play() {
    if (this._wantsPlaying) return;
    this._wantsPlaying = true;
    if (this._activeId !== null) {
      try { this._howl.play(this._activeId); } catch {}
      this._scheduleCrossfade();
    } else if (this._howl.state() === 'loaded') {
      this._begin();
    }
    // else: still loading - the 'load' listener above calls _begin() once ready.
  }

  pause() {
    this._wantsPlaying = false;
    clearTimeout(this._crossfadeTimer);
    if (this._activeId !== null) {
      try { this._howl.pause(this._activeId); } catch {}
    }
  }

  stop() {
    this._wantsPlaying = false;
    clearTimeout(this._crossfadeTimer);
    clearTimeout(this._stopTimer);
    try { this._howl.stop(); } catch {}
    this._activeId = null;
  }

  // stop() alone keeps the decoded AudioBuffer alive: Howler holds it in its URL cache and the
  // Howl in Howler._howls until unload(). A 60-90 s ambience loop decodes to 20-35 MB, so every
  // loop that is merely dropped leaks that much for the rest of the session (iOS jetsam, see
  // js/ui/memProbe.js). Call this when the loop is no longer needed; the object is dead afterwards.
  unload() {
    this._dead = true;
    this.stop();
    try { this._howl.unload(); } catch {}
  }

  playing() {
    return this._wantsPlaying;
  }

  volume(v) {
    if (v === undefined) return this._vol;
    this._vol = v;
    if (this._activeId !== null) {
      try { this._howl.volume(v, this._activeId); } catch {}
    }
    return this._vol;
  }

  rate(r) {
    if (r === undefined) return this._rate;
    this._rate = r;
    if (this._activeId !== null) {
      try { this._howl.rate(r, this._activeId); } catch {}
    }
    return this._rate;
  }
}

// Unit-specific SFX warmed when the unit's card enters the hand (see warmUnitSamples()).
const UNIT_WARM_SAMPLES = {
  Hacker: [
    'more/sci_fi/hacker_harpoon.m4a',
    'more/sci_fi/hacker_death.m4a',
    'more/sci_fi/hacker_transcendence.m4a',
    'more/sci_fi/mid_frequency_flutter_fx_01.m4a'
  ]
};

export class SoundEngine {
  constructor() {
    this.ctx = null;
    this.bgm = null; // currently playing music <audio> element, if any
    this._musicContext = null; // null | 'menu' | 'map' | 'campaign-demo'
    this._musicMapId = null;
    this._lastOstFile = null;
    this._iosAudioSessionUnlocked = false;
    this._isBackgrounded = false;
    this._bgmPausedByVisibility = false;
    this._ambiencePausedByVisibility = false;
    this._touchResumeArmed = false;
    this._touchResumeHandler = null;
    this._resumeInProgress = false;
    // 3-state Sound-Button / Sliders: default is SFX on + Music enabled in menu
    let defaultMuted = false;
    let defaultMusicEnabled = true;
    let defaultMusicVol = 0.7;
    let defaultSfxVol = 0.6;
    try {
      if (typeof localStorage !== 'undefined') {
        const storedMVol = localStorage.getItem('ucg_music_volume');
        if (storedMVol !== null) defaultMusicVol = parseFloat(storedMVol);
        const storedSVol = localStorage.getItem('ucg_sfx_volume');
        if (storedSVol !== null) defaultSfxVol = parseFloat(storedSVol);

        const stored = localStorage.getItem('ucg_sound_muted');
        const storedMusic = localStorage.getItem('ucg_music_enabled');

        // If volume was stored, volume slider is the primary authority:
        // vol > 0 means unmuted and music enabled (unless explicitly disabled via storedMusic).
        if (storedMVol !== null || storedSVol !== null) {
          defaultMuted = (defaultMusicVol === 0 && defaultSfxVol === 0);
          defaultMusicEnabled = defaultMusicVol > 0;
          if (storedMusic !== null) {
            defaultMusicEnabled = storedMusic === 'true' && defaultMusicVol > 0;
          }
        } else {
          if (stored !== null) {
            defaultMuted = stored === 'true';
          }
          if (storedMusic !== null) {
            defaultMusicEnabled = storedMusic === 'true';
          }
        }
      }
    } catch {}
    this.musicVolume = isNaN(defaultMusicVol) ? 0.7 : Math.max(0, Math.min(1, defaultMusicVol));
    this.sfxVolume = isNaN(defaultSfxVol) ? 0.6 : Math.max(0, Math.min(1, defaultSfxVol));
    this.isMuted = defaultMuted;
    this.musicEnabled = defaultMusicEnabled && this.musicVolume > 0;
    this.registry = SOUND_REGISTRY;
    this._lastPickedInPool = new Map();
    this._currentBgmBaseVolume = 1.0;
    this._setupAudioSession();
    this.isDucking = false;
    this._bgmDuckTimer = null;
    this._combatDucking = false;
    this._combatDuckFactor = 1.0;
    this._combatDuckTimer = null;
    this._matchTension = 0.0;
    this.initialized = false;

    // Cross-tab real-time sound sync with Sound Review Studio
    if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
      try {
        this._registryChannel = new BroadcastChannel('ucg_sound_registry');
        this._registryChannel.onmessage = (e) => {
          if (e.data && e.data.registry) {
            this.registry = e.data.registry;
            if (this._musicContext === 'menu') {
              this._applyLiveMusicVolume();
            }
          }
        };
      } catch {}
    }
    if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
      this.refreshRegistry().catch(() => {});
    }
    this._initMediaSession();
  }

  async refreshRegistry() {
    // The constructor and init() both ask for the registry at boot - one
    // network round trip (with a cache-busting query, i.e. never cacheable)
    // is enough; admin edits additionally arrive live via BroadcastChannel.
    // Only query the live dev endpoint on localhost/127.0.0.1 (Sound Review Studio);
    // in production (voidconflict.com), SOUND_REGISTRY is already statically bundled.
    if (typeof window !== 'undefined' && window.location?.hostname) {
      const host = window.location.hostname;
      const isLocalDev = host === 'localhost' || host === '127.0.0.1' || host === '::1' || window.__enableSoundRegistryFetch;
      if (!isLocalDev) return this.registry;
    }
    if (this._registryInflight) return this._registryInflight;
    const now = Date.now();
    if (this._registryFetchedAt && now - this._registryFetchedAt < 30000) return this.registry;
    this._registryInflight = (async () => {
      try {
        if (typeof window !== 'undefined' && typeof fetch !== 'undefined') {
          const res = await fetch('api/sound-registry?t=' + Date.now());
          if (res.ok) {
            const data = await res.json();
            if (data && data.registry) {
              this.registry = data.registry;
              this._registryFetchedAt = Date.now();
              if (this._musicContext === 'menu') {
                this._applyLiveMusicVolume();
              }
            }
          }
        }
      } catch {}
      this._registryInflight = null;
      return this.registry;
    })();
    return this._registryInflight;
  }

  _getAbsoluteUrl(relPath) {
    try {
      if (typeof window !== 'undefined' && window.location?.href) {
        return new URL(relPath, window.location.href).href;
      }
    } catch (_) {}
    return relPath;
  }

  _initMediaSession() {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      this._updateMediaSession();

      if (typeof navigator.mediaSession.setActionHandler === 'function') {
        navigator.mediaSession.setActionHandler('play', () => {
          if (this.isMuted) {
            this.isMuted = false;
          }
          if (this._musicContext === 'menu' && this.musicEnabled && this.bgm?.paused) {
            this.bgm.play().catch(() => {});
          }
          this._updateMediaSession();
        });

        navigator.mediaSession.setActionHandler('pause', () => {
          if (this.bgm && !this.bgm.paused) {
            this.bgm.pause();
          }
          if (navigator.mediaSession) {
            navigator.mediaSession.playbackState = 'paused';
          }
        });
      }
    } catch (_) {}
  }

  _updateMediaSession(trackName = null) {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') {
      return;
    }
    try {
      const icon512 = this._getAbsoluteUrl('assets/ui/icons/icon-512.png');
      const icon192 = this._getAbsoluteUrl('assets/ui/icons/icon-192.png');
      const iconTouch = this._getAbsoluteUrl('assets/ui/icons/apple-touch-icon.png');

      const isPlaying = !this.isMuted && this.musicEnabled && this._musicContext === 'menu' && this.bgm && !this.bgm.paused && !this._isBackgrounded;

      let displayTrack = 'VOID Conflict Soundtrack';
      const effectiveTrack = trackName || this._currentBgmTrack;
      if (typeof effectiveTrack === 'string' && effectiveTrack.trim()) {
        const cleanName = effectiveTrack.split('/').pop().replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
        displayTrack = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
      }

      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'VOID Conflict',
        artist: 'VOID Conflict',
        album: displayTrack,
        artwork: [
          { src: icon512, sizes: '512x512', type: 'image/png' },
          { src: icon192, sizes: '192x192', type: 'image/png' },
          { src: iconTouch, sizes: '180x180', type: 'image/png' }
        ]
      });

      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (_) {}
  }

  _setupAudioSession() {
    if (typeof navigator !== 'undefined' && 'audioSession' in navigator) {
      try {
        if (navigator.audioSession.type !== 'playback') {
          navigator.audioSession.type = 'playback';
        }
      } catch (_) {}
    }
  }

  // Unlocks Web Audio & activates the iOS Media (Playback) audio session channel.
  // On iOS Safari / WebKit, Web Audio (Howler SFX) is routed through the Ambient
  // category by default, which is completely silenced whenever the physical mute/silent
  // switch is engaged. Playing an HTML5 audio element (even a tiny silent buffer) inside
  // a user gesture activates AVAudioSessionCategoryPlayback for the WebKit process,
  // allowing SFX to be heard even when music is disabled and the iPhone is set to silent.
  unlockAudioSession() {
    this._setupAudioSession();

    if (this.ctx && typeof this.ctx.resume === 'function' && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }

    if (this._iosAudioSessionUnlocked) {
      return Promise.resolve();
    }
    if (this._unlockSessionPromise) {
      return this._unlockSessionPromise;
    }

    // If music is already enabled and menu music is playing or can play, that already
    // activates the Playback category.
    if (!this.isMuted && this.musicEnabled && this._musicContext === 'menu' && this.bgm) {
      if (this.bgm.paused) {
        const p = this.bgm.play();
        if (p && typeof p.then === 'function') {
          this._unlockSessionPromise = p.then(() => {
            this._iosAudioSessionUnlocked = true;
          }).catch(() => {
            // Autoplay blocked on this gesture; harmless
          }).finally(() => {
            this._unlockSessionPromise = null;
          });
          return this._unlockSessionPromise;
        }
      }
      this._iosAudioSessionUnlocked = true;
      return Promise.resolve();
    }

    if (typeof Audio === 'undefined') {
      this._iosAudioSessionUnlocked = true;
      return Promise.resolve();
    }

    try {
      const silent = new Audio();
      silent.setAttribute('x-webkit-airplay', 'deny');
      silent.preload = 'auto';
      // 1-frame silent MP3 data URI (zero network, zero latency)
      silent.src = 'data:audio/mp3;base64,//OEAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAAA8TEFNRTMuMTAw//OEAAAAAAAAAAAAAAAAAAAAAAAHNAAAAAAUAAAK8AAAAAAAAAAA////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////';

      const p = silent.play();
      if (p && typeof p.then === 'function') {
        this._unlockSessionPromise = p.then(() => {
          this._iosAudioSessionUnlocked = true;
          try {
            silent.pause();
          } catch (_) {}
        }).catch(() => {
          // May be rejected on non-activating gestures or format policy; harmless
        }).finally(() => {
          this._unlockSessionPromise = null;
        });
        return this._unlockSessionPromise;
      } else {
        this._iosAudioSessionUnlocked = true;
        try {
          silent.pause();
        } catch (_) {}
        return Promise.resolve();
      }
    } catch (_) {
      this._iosAudioSessionUnlocked = true;
      return Promise.resolve();
    }
  }

  init() {
    if (this.initialized) return;
    this.refreshRegistry().catch(() => {});
    if (typeof window === 'undefined') return;
    try {
      this._setupAudioSession();
      // Reuse Howler's single shared AudioContext instead of creating a
      // second, independent one - two live WebAudio contexts (one per
      // engine) mean two parallel hardware render threads for the whole
      // session, real CPU/battery overhead for no benefit
      // (docs/ONLINE_PERFORMANCE_AND_CARD_LAG_ANALYSIS.md "Double
      // AudioContext"). `Howler.volume()` with no argument is Howler's own
      // public, side-effect-free (besides lazy setup) read call - it runs
      // through the exact same ctx+masterGain bootstrap every Howl() would
      // trigger anyway (js/vendor/howler.js), so calling it here just
      // makes that setup happen now instead of on whatever sample plays
      // first, and does it Howler's way rather than us hand-rolling a
      // second context/masterGain graph.
      if (window.Howler) {
        window.Howler.volume();
        this.ctx = window.Howler.ctx || null;
      }
      if (!this.ctx) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AudioContext();
      }
      this.initialized = true;
      this._initMediaSession();
      if (this.isMuted) {
        this._suspendContextIfNeeded();
      } else if (this._musicContext === 'menu' && this.musicEnabled && this.bgm?.paused) {
        this.bgm.play().catch(() => {});
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this._onBackground();
          } else {
            this._onForeground();
          }
        });
      }
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => this._onBackground());
        window.addEventListener('pageshow', () => this._onForeground());
      }
      this.preloadCommonSamples();
    } catch (e) {
      console.warn("Web Audio not supported or blocked", e);
    }
  }

  _suspendContextIfNeeded() {
    try {
      if (this.ctx && typeof this.ctx.suspend === 'function' && this.ctx.state === 'running') {
        this.ctx.suspend().catch(() => {});
      }
    } catch (_) {}
  }

  _resumeContextIfNeeded() {
    this._setupAudioSession();
    try {
      if (this.ctx && typeof this.ctx.resume === 'function' && (this.ctx.state === 'suspended' || this.ctx.state === 'interrupted')) {
        this.ctx.resume().catch(() => {});
      }
    } catch (_) {}
    if (this._musicContext === 'menu' && !this.isMuted && this.musicEnabled && this.bgm?.paused && !this._isBackgrounded && !this._bgmPausedByVisibility) {
      this.bgm.play().catch(() => {});
    }
  }

  _onBackground() {
    if (this._isBackgrounded) return;
    this._isBackgrounded = true;
    this._removeVisibilityTouchResume();

    // 1. Pause active menu music
    if (this.bgm && !this.bgm.paused) {
      this._bgmPausedByVisibility = true;
      try { this.bgm.pause(); } catch (_) {}
    }

    // 2. Pause active ambience (e.g. battlefield ambience in match)
    if (this._ambience && this._ambience.playing?.()) {
      this._ambiencePausedByVisibility = true;
      try { this._ambience.pause(); } catch (_) {}
    }

    // 3. Suspend Web Audio context to stop all SFX/oscillators
    this._suspendContextIfNeeded();

    // 4. Update iOS MediaSession state to 'paused'
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.playbackState = 'paused';
      } catch (_) {}
    }
  }

  _onForeground() {
    if (!this._isBackgrounded) return;
    this._isBackgrounded = false;

    // 1. Resume Web Audio context
    this._resumeContextIfNeeded();

    // 2. Resume menu music if it was playing before entering background
    let bgmPromise = null;
    if (this._bgmPausedByVisibility && this.bgm && !this.isMuted && this.musicEnabled && this._musicContext === 'menu') {
      try {
        bgmPromise = this.bgm.play?.();
      } catch (_) {}
    } else {
      this._bgmPausedByVisibility = false;
    }

    // 3. Resume match ambience if it was playing before entering background
    if (this._ambiencePausedByVisibility && this._ambience && !this.isMuted && this.musicEnabled) {
      try {
        this._ambience.play();
        this._ambiencePausedByVisibility = false;
      } catch (_) {
        this._armVisibilityTouchResume();
      }
    } else {
      this._ambiencePausedByVisibility = false;
    }

    if (bgmPromise && typeof bgmPromise.then === 'function') {
      bgmPromise.then(() => {
        this._bgmPausedByVisibility = false;
        this._removeVisibilityTouchResume();
        this._updateMediaSession();
      }).catch(() => {
        // iOS Safari blocked programmatic playback without gesture -> arm invisible one-touch fallback
        this._armVisibilityTouchResume();
      });
    } else {
      this._updateMediaSession();
    }
  }

  _armVisibilityTouchResume() {
    if (this._touchResumeArmed || typeof document === 'undefined') return;
    this._touchResumeArmed = true;

    const events = ['click', 'touchend', 'pointerup', 'pointerdown'];
    const handler = () => {
      if (this._resumeInProgress) return;

      if (this.ctx && (this.ctx.state === 'suspended' || this.ctx.state === 'interrupted')) {
        try { this.ctx.resume().catch(() => {}); } catch (_) {}
      }

      let bgmPromise = null;
      if (this._bgmPausedByVisibility && this.bgm && this.bgm.paused && !this.isMuted && this.musicEnabled && this._musicContext === 'menu') {
        try {
          bgmPromise = this.bgm.play?.();
        } catch (_) {}
      } else {
        this._bgmPausedByVisibility = false;
      }

      if (this._ambiencePausedByVisibility && this._ambience && !this.isMuted && this.musicEnabled) {
        try {
          this._ambience.play();
          this._ambiencePausedByVisibility = false;
        } catch (_) {}
      }

      if (bgmPromise && typeof bgmPromise.then === 'function') {
        this._resumeInProgress = true;
        bgmPromise.then(() => {
          this._resumeInProgress = false;
          this._bgmPausedByVisibility = false;
          this._removeVisibilityTouchResume();
          this._updateMediaSession();
        }).catch(() => {
          this._resumeInProgress = false;
          // Keep handler armed until a valid gesture (like touchend/click) succeeds in Safari
        });
      } else {
        this._removeVisibilityTouchResume();
        this._updateMediaSession();
      }
    };

    this._touchResumeHandler = handler;
    for (const ev of events) {
      document.addEventListener(ev, handler, { capture: true, passive: true });
    }
  }

  _removeVisibilityTouchResume() {
    if (!this._touchResumeArmed) return;
    this._touchResumeArmed = false;
    if (this._touchResumeHandler && typeof document !== 'undefined') {
      const events = ['click', 'touchend', 'pointerup', 'pointerdown'];
      for (const ev of events) {
        document.removeEventListener(ev, this._touchResumeHandler, true);
      }
      this._touchResumeHandler = null;
    }
  }

  // Warms the Howler sample cache (_getSample()/_samples Map) for the
  // handful of file-based SFX virtually every match needs within its first
  // few seconds - card play/draw, the two active races' spawn barks, the
  // generic error blip. Without this, the very first time any of these
  // plays, _getSample() constructs a brand-new Howl right then, which
  // kicks off a network fetch + WebAudio decode at that exact instant -
  // the first card played in a match would visibly/audibly stutter for
  // however long that cold fetch+decode takes
  // (docs/ONLINE_PERFORMANCE_AND_CARD_LAG_ANALYSIS.md). Called once from
  // init() (the first pointerdown anywhere - see js/main.js), which is
  // already well before a match actually starts (deck selection, lobby),
  // giving the browser time to fetch+decode in the background. Constructing
  // a Howl already begins loading it (Howler's default preload:true) - we
  // never need to call .play() here, just _getSample() each file once.
  preloadCommonSamples() {
    if (typeof window === 'undefined' || !window.Howl) return;
    const files = new Set(['card-place-2.ogg', 'bookFlip1.ogg', 'error_006.ogg']);
    if (this.registry?.ui) {
      for (const key of ['card_play', 'card_draw', 'card_discard', 'error']) {
        const ev = this.registry.ui[key];
        if (ev && Array.isArray(ev.files)) {
          for (const f of ev.files) files.add(f);
        }
      }
    }
    for (const race of Object.keys(BARK_FILES)) {
      for (const entry of Object.values(BARK_FILES[race])) {
        const file = typeof entry === 'object' && entry !== null ? entry.file : entry;
        if (file) files.add(file);
      }
    }
    this._pinSamples(files);
    for (const file of files) {
      this._getSample(file);
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      this._bgmPausedByVisibility = false;
      this._ambiencePausedByVisibility = false;
      this._removeVisibilityTouchResume();
    }
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('ucg_sound_muted', String(this.isMuted));
      }
    } catch {}
    if (this.bgm) {
      if (this.isMuted) {
        this.bgm.volume = 0;
        this.bgm.pause?.();
      } else {
        this.bgm.volume = this.isDucking ? (this.musicVolume * 0.15) : this.musicVolume;
        this.bgm.play?.()?.catch?.(() => {});
      }
    }
    if (this.isMuted) {
      this._suspendContextIfNeeded();
    } else {
      this._resumeContextIfNeeded();
    }
    this._updateMediaSession();
    return this.isMuted;
  }

  // 3-state Sound-Button (SFX only -> With Music -> No Sound -> ...), das
  // ist es, was die UI tatsächlich aufruft (js/main.js). toggleMute() oben
  // bleibt unverändert nur für den bestehenden Test-Vertrag
  // (tests/bio_plasma_test.mjs) erhalten, hat aber keinen Aufrufer mehr
  // außerhalb davon.
  cycleSoundMode() {
    const order = ['sfx', 'music', 'off'];
    const current = this.isMuted ? 'off' : (this.musicEnabled ? 'music' : 'sfx');
    const next = order[(order.indexOf(current) + 1) % order.length];

    this.isMuted = next === 'off';
    this.musicEnabled = next === 'music';

    if (this.isMuted || !this.musicEnabled) {
      this._bgmPausedByVisibility = false;
      this._ambiencePausedByVisibility = false;
      this._removeVisibilityTouchResume();
    }

    if (next === 'music' && this.musicVolume <= 0) {
      this.musicVolume = 0.7;
    }
    if (next === 'sfx') {
      this.unlockAudioSession()?.catch?.(() => {});
      if (this.sfxVolume <= 0) {
        this.sfxVolume = 0.6;
      }
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('ucg_sound_muted', String(this.isMuted));
        localStorage.setItem('ucg_music_enabled', String(this.musicEnabled));
        if (next === 'music') {
          localStorage.setItem('ucg_music_volume', String(this.musicVolume));
        }
      }
    } catch {}

    // Musik darf AUSSCHLIESSLICH im Menü abgespielt werden (User-Vorgabe: "die musik soll nur im menü abgespielt werden")
    if (this._musicContext === 'menu') {
      if (this.isMuted || !this.musicEnabled) {
        this.bgm?.pause?.();
      } else {
        if (!this.bgm) {
          this.playMenuMusic();
        } else {
          this.bgm.volume = this.isDucking ? (this.musicVolume * 0.15) : this.musicVolume;
          this.bgm.play?.()?.catch?.(() => {});
        }
      }
    } else {
      // Während des Spiels / im Match: Musik bleibt immer stumm und pausiert
      this.bgm?.pause?.();
    }

    // Ambience (Batch 4, docs/SOUND_BATCH_PLAN.md section 4.4) folgt
    // demselben Schalter wie Musik - Atmosphäre gehört zum "SFX+Music"-
    // Erlebnis, nicht zum "SFX only"-Standard.
    if (this._ambience) {
      if (this.isMuted || !this.musicEnabled) {
        this._ambience.pause();
      } else {
        this._ambience.play();
      }
    }

    if (this.isMuted) {
      this._suspendContextIfNeeded();
      this.stopFlamethrower?.(true);
      this._a9MotorHowl?.pause();
    } else {
      this._resumeContextIfNeeded();
      // Ein bereits laufender A9-Piranha-Motor-Loop (siehe startA9PiranhaMotorLoop()
      // unten) pausiert oben beim Stummschalten, bekommt aber - anders als
      // Flamethrower - keinen neuen play()-Aufruf durch laufende Kampfhandlungen
      // nachgeliefert, da er nur einmal pro Einheit beim Spawn gestartet wird.
      if (this._a9MotorHowl && (this._a9MotorRefCount || 0) > 0 && !this._a9MotorHowl.playing()) {
        this._a9MotorHowl.volume(Math.max(0, Math.min(1, 0.1 * this.sfxVolume)));
        this._a9MotorHowl.play();
      }
    }

    this._updateMediaSession();
    return next;
  }

  getSoundMode() {
    return this.isMuted ? 'off' : (this.musicEnabled ? 'music' : 'sfx');
  }

  getMusicVolume() {
    return this.musicVolume;
  }

  getSfxVolume() {
    return this.sfxVolume;
  }

  setMusicVolume(vol, persist = true) {
    this.musicVolume = Math.max(0, Math.min(1, vol));
    this.musicEnabled = this.musicVolume > 0;
    if (this.musicVolume > 0 && this.isMuted) {
      this.isMuted = false;
      this._resumeContextIfNeeded();
    }
    if (this.musicVolume === 0 && this.sfxVolume > 0 && !this.isMuted) {
      this.unlockAudioSession()?.catch?.(() => {});
    }
    if (persist) {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('ucg_music_volume', String(this.musicVolume));
          localStorage.setItem('ucg_music_enabled', String(this.musicEnabled));
          localStorage.setItem('ucg_sound_muted', String(this.isMuted));
        }
      } catch {}
    }
    if (this.bgm && !this.isMuted) {
      const baseVol = typeof this._currentBgmBaseVolume === 'number' ? this._currentBgmBaseVolume : 1.0;
      this.bgm.volume = (this.isDucking ? (this.musicVolume * 0.15) : this.musicVolume) * baseVol;
    }
    if (this._musicContext === 'menu') {
      if (this.musicVolume === 0 || this.isMuted) {
        this._bgmPausedByVisibility = false;
        this._removeVisibilityTouchResume();
        this.bgm?.pause?.();
      } else {
        if (!this.bgm) {
          this.playMenuMusic();
        } else if (this.bgm.paused) {
          this.bgm.play?.()?.catch?.(() => {});
        }
      }
    }
    this._updateMediaSession();
  }

  setSfxVolume(vol, persist = true) {
    this.sfxVolume = Math.max(0, Math.min(1, vol));
    if (this.sfxVolume > 0) {
      if (this.isMuted) {
        this.isMuted = false;
        this._resumeContextIfNeeded();
      }
      if (persist && !this._iosAudioSessionUnlocked) {
        this.unlockAudioSession()?.catch?.(() => {});
      }
    }
    if (persist) {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('ucg_sfx_volume', String(this.sfxVolume));
          localStorage.setItem('ucg_sound_muted', String(this.isMuted));
        }
      } catch {}
    }
    if (this._flamethrowerHowl && this._flamethrowerActive) {
      this._flamethrowerHowl.volume(Math.max(0, Math.min(1, 0.7 * this.sfxVolume * 0.8)));
    }
    if (this._a9MotorHowl && (this._a9MotorRefCount || 0) > 0) {
      this._a9MotorHowl.volume(Math.max(0, Math.min(1, 0.1 * this.sfxVolume)));
    }
    if (this._ambience) {
      const targetVol = (0.28 + (this._matchTension || 0) * 0.14) * this.sfxVolume;
      try {
        this._ambience.volume(Math.max(0, Math.min(1, targetVol)));
      } catch {}
    }
  }

  // 2D Spatial Stereo Panning (Maßnahme 2)
  // Battlefield width is 1600px. Center is 800px.
  // Panning is clamped to [-0.75, +0.75] to avoid hard cutoffs in one ear.
  panFromX(x) {
    if (typeof x !== 'number' || isNaN(x)) return 0;
    const norm = (x - 800) / 800;
    return Math.max(-0.75, Math.min(0.75, Math.round(norm * 0.75 * 1000) / 1000));
  }

  // Highlight-Ducking (Maßnahme 4)
  // Dämpft während großer Ereignisse (Nuke, Base Alarm, Crash, etc.)
  // reguläre Infanterie-Schüsse (combat_infantry) um 40%, um Wumms und Klarheit zu schaffen.
  triggerCombatDucking(durationMs = 450, duckFactor = 0.6) {
    this._combatDucking = true;
    this._combatDuckFactor = duckFactor;
    if (this._combatDuckTimer) {
      clearTimeout(this._combatDuckTimer);
      this._combatDuckTimer = null;
    }
    if (this._activeVoices) {
      for (const v of this._activeVoices) {
        if (v.category === 'combat_infantry' && v.sample) {
          try {
            const baseVol = v.baseVolume ?? 1;
            const newVol = Math.max(0, Math.min(1, baseVol * this.sfxVolume * duckFactor));
            v.sample.volume(newVol, v.id);
          } catch {}
        }
      }
    }
    this._combatDuckTimer = setTimeout(() => {
      this._combatDucking = false;
      this._combatDuckFactor = 1.0;
      this._combatDuckTimer = null;
    }, durationMs);
  }

  // Dynamischer Ambience-Puls & Match Tension (Maßnahme 8)
  // Skaliert die Ambience basierend auf Match-Dramatik (Basen-HP & Einheiten-Dichte).
  setMatchTension(tension) {
    this._matchTension = Math.max(0, Math.min(1, typeof tension === 'number' ? tension : 0));
    if (this._ambience) {
      const targetVol = (0.28 + this._matchTension * 0.14) * this.sfxVolume;
      try {
        this._ambience.volume(Math.max(0, Math.min(1, targetVol)));
      } catch {}
      const targetRate = 1.0 + this._matchTension * 0.03;
      try {
        this._ambience.rate(targetRate);
      } catch {}
    }
  }

  getMatchTension() {
    return this._matchTension || 0;
  }

  // Sul Bio-Plasma BGM Ducking (docs/BIO_PLASMA_PLAN.md §7.7 / §10.3)
  duckBGM(duckFactor = 0.15, duration = 1.95) {
    if (this._bgmDuckTimer) {
      clearTimeout(this._bgmDuckTimer);
      this._bgmDuckTimer = null;
    }
    this.isDucking = true;
    const baseVol = typeof this._currentBgmBaseVolume === 'number' ? this._currentBgmBaseVolume : 1.0;
    if (this.bgm && !this.isMuted) {
      this.bgm.volume = Math.max(0, Math.min(1, this.musicVolume * duckFactor * baseVol));
    }
    this._bgmDuckTimer = setTimeout(() => {
      this.isDucking = false;
      this._bgmDuckTimer = null;
      if (this.bgm && !this.isMuted) {
        const curBase = typeof this._currentBgmBaseVolume === 'number' ? this._currentBgmBaseVolume : 1.0;
        this.bgm.volume = Math.max(0, Math.min(1, this.musicVolume * curBase));
      }
    }, duration * 1000);
  }

  // ---------------------------------------------------------------------
  // Music Director (docs/about music folder.rtf, docs/SOUND_BATCH_PLAN.md
  // section 5). Crossfades `this.bgm` between tracks under assets/music/ or assets/sound/ -
  // "immer... fade out... oder crossfade... weiche Übergänge" is the
  // default; the one documented exception is the campaign-demo exit, which
  // is an explicit hard cut (stopCampaignDemoMusicHardCut()).
  // ---------------------------------------------------------------------

  _resolveMusicPath(relPath) {
    if (!relPath) return '';
    const p = relPath.startsWith('/') ? relPath.slice(1) : relPath;
    if (p.startsWith('assets/')) {
      return p;
    }
    if (p.startsWith('music/')) {
      return 'assets/' + p;
    }
    if (p.startsWith('sound/')) {
      return 'assets/' + p;
    }
    if (p.startsWith('OST/') || p.startsWith('short/') || p.startsWith('var/') || (p.startsWith('map/') && p.endsWith('.mp3'))) {
      return 'assets/music/' + p;
    }
    if (p.startsWith('even more sounds/') || p.startsWith('more sound/') || p.startsWith('more/') || p.startsWith('baked/')) {
      return 'assets/sound/' + p;
    }
    return MUSIC_BASE + p;
  }

  _crossfadeMusicTo(relPath, { loop = false, fadeMs = 1200, onEnded = null, baseVolume = null } = {}) {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
    const outgoing = this.bgm;
    const cleanPath = this._resolveMusicPath(relPath);
    this._currentBgmTrack = relPath;
    const incoming = new Audio(encodeURI(cleanPath));
    incoming.loop = loop;
    incoming.volume = 0;
    if (onEnded) incoming.addEventListener('ended', onEnded);
    incoming.addEventListener('play', () => {
      this._updateMediaSession(relPath);
    });
    incoming.addEventListener('pause', () => {
      if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
        try {
          navigator.mediaSession.playbackState = 'paused';
        } catch (_) {}
      }
    });
    this.bgm = incoming;
    this._updateMediaSession(relPath);
    // Musik darf AUSSCHLIESSLICH im Menü abgespielt werden:
    if (!this.isMuted && this.musicEnabled && this._musicContext === 'menu') {
      incoming.play().catch(() => {});
    }
    const regVol = typeof baseVolume === 'number'
      ? baseVolume
      : (typeof this.registry?.music?.menu_ost?.volume === 'number'
        ? this.registry.music.menu_ost.volume
        : 1.0);
    this._currentBgmBaseVolume = regVol;
    const targetVolume = (this.isDucking ? this.musicVolume * 0.15 : this.musicVolume) * regVol;
    this._fadeAudioElement(incoming, (this._musicContext === 'menu' ? targetVolume : 0), fadeMs);
    if (outgoing && outgoing !== incoming) this._fadeAudioElement(outgoing, 0, fadeMs, () => outgoing.pause?.());
    return incoming;
  }

  // Hard cut, no fade - only for the one documented exception above.
  _stopMusicHardCut() {
    this._bgmPausedByVisibility = false;
    this._removeVisibilityTouchResume();
    this.bgm?.pause?.();
    this.bgm = null;
    this._currentBgmTrack = null;
    this._currentBgmBaseVolume = 1.0;
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.playbackState = 'paused';
      } catch (_) {}
    }
  }

  // Musik darf ausschließlich im Menü abgespielt werden (User-Vorgabe 2026-09).
  // Stoppt beim Starten eines Spiels/Matches sofort jegliche Musik.
  stopMusic(fadeMs = 300) {
    this._musicContext = 'match';
    this._musicMapId = null;
    this._currentBgmTrack = null;
    this._currentBgmBaseVolume = 1.0;
    this._bgmPausedByVisibility = false;
    this._removeVisibilityTouchResume();
    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.playbackState = 'paused';
      } catch (_) {}
    }
    if (this.bgm) {
      const oldBgm = this.bgm;
      this.bgm = null;
      if (fadeMs > 0) {
        this._fadeAudioElement(oldBgm, 0, fadeMs, () => {
          oldBgm.pause?.();
        });
      } else {
        oldBgm.pause?.();
      }
    }
  }

  stopMenuMusic(fadeMs = 300) {
    this.stopMusic(fadeMs);
  }

  _fadeAudioElement(el, toVolume, durationMs, onDone = null) {
    if (!el) return;
    if (el._ucgFadeTimer) clearInterval(el._ucgFadeTimer);
    const fromVolume = el.volume;
    const steps = Math.max(1, Math.round(durationMs / 50));
    let step = 0;
    el._ucgFadeTimer = setInterval(() => {
      step++;
      const t = step / steps;
      el.volume = Math.max(0, Math.min(1, fromVolume + (toVolume - fromVolume) * t));
      if (step >= steps) {
        clearInterval(el._ucgFadeTimer);
        el._ucgFadeTimer = null;
        el.volume = toVolume;
        onDone?.();
      }
    }, 50);
  }

  // Menu music:
  // - Start-Pool: menu_ost (wählt zufällig mit Anti-Repetition beim Start/Menüeinstieg)
  // - Rotations-Pool: menu_rotation_pool (überblendet weich von Track zu Track, sobald der vorherige Track endet)
  // Idempotent: Solange man im Menü ist und Musik läuft, unterbricht ein Navigieren im Menü die Musik nicht.
  playMenuMusic(force = false) {
    this.stopCombatSounds();
    this.stopAmbience();

    const startDef = this.registry?.music?.menu_ost;
    const startFiles = Array.isArray(startDef?.files) && startDef.files.length > 0
      ? startDef.files
      : [OST_ONSTART_FILE];
    const rotationDef = this.registry?.music?.menu_rotation_pool;
    const rotationFiles = Array.isArray(rotationDef?.files) && rotationDef.files.length > 0
      ? rotationDef.files
      : [];

    if (this._musicContext === 'menu' && this.bgm && !force) {
      if (!this.isMuted && this.musicEnabled && this.bgm.paused) {
        this.bgm.play().catch(() => {});
      }
      return;
    }

    this._musicContext = 'menu';
    this._musicMapId = null;

    const track = this._pickFromPool(startFiles, 'menu_ost_start') || startFiles[0];
    const baseVolume = typeof startDef?.volume === 'number' ? startDef.volume : 0.15;
    const canRotate = rotationFiles.length > 0 || startFiles.length > 1;

    this._crossfadeMusicTo(track, {
      loop: !canRotate,
      baseVolume,
      onEnded: canRotate ? () => this._continueMenuRotation() : null
    });
  }

  _continueMenuRotation() {
    if (this._musicContext !== 'menu') return;

    const rotationDef = this.registry?.music?.menu_rotation_pool;
    let pool = Array.isArray(rotationDef?.files) && rotationDef.files.length > 0
      ? rotationDef.files
      : null;
    let baseVolume = typeof rotationDef?.volume === 'number' ? rotationDef.volume : 0.20;

    if (!pool || pool.length === 0) {
      const startDef = this.registry?.music?.menu_ost;
      pool = Array.isArray(startDef?.files) && startDef.files.length > 0
        ? startDef.files
        : [OST_ONSTART_FILE];
      baseVolume = typeof startDef?.volume === 'number' ? startDef.volume : 0.15;
    }

    const nextTrack = this._pickFromPool(pool, 'menu_rotation') || pool[0];
    const canRotate = pool.length > 1;

    this._crossfadeMusicTo(nextTrack, {
      loop: !canRotate,
      baseVolume,
      onEnded: canRotate ? () => this._continueMenuRotation() : null
    });
  }

  _continueOstRotation() {
    this._continueMenuRotation();
  }

  _pickOstRotationFile() {
    const rotationDef = this.registry?.music?.menu_rotation_pool;
    const pool = Array.isArray(rotationDef?.files) && rotationDef.files.length > 0
      ? rotationDef.files
      : OST_ROTATION_FILES;
    return this._pickFromPool(pool, 'menu_rotation') || pool[0];
  }

  _applyLiveMusicVolume() {
    if (this._musicContext !== 'menu' || !this.bgm || this.isMuted) return;
    const track = this._currentBgmTrack;
    let newBaseVol = null;
    const ostFiles = this.registry?.music?.menu_ost?.files || [];
    const rotFiles = this.registry?.music?.menu_rotation_pool?.files || [];
    if (ostFiles.includes(track) && typeof this.registry?.music?.menu_ost?.volume === 'number') {
      newBaseVol = this.registry.music.menu_ost.volume;
    } else if (rotFiles.includes(track) && typeof this.registry?.music?.menu_rotation_pool?.volume === 'number') {
      newBaseVol = this.registry.music.menu_rotation_pool.volume;
    }
    if (typeof newBaseVol === 'number') {
      this._currentBgmBaseVolume = newBaseVol;
      const targetVolume = (this.isDucking ? this.musicVolume * 0.15 : this.musicVolume) * newBaseVol;
      this._fadeAudioElement(this.bgm, targetVolume, 300);
    }
  }

  // Per-map music tracks have been removed in favor of continuous battlefield ambience
  // (playBattlefieldAmbience() / drone_low_pulse.m4a) to keep matches atmospheric and un-intrusive.
  // Method kept as no-op to preserve API compatibility across match modes.
  playMapMusic(mapId) {
    // Intentionally silenced - matches use playBattlefieldAmbience() instead of music tracks.
    this.stopMusic();
    this.playBattlefieldAmbience();
  }

  _startMapLoopHandoff(mapId) {
    // No-op - map loop handoff disabled.
  }

  // Singleplayer-campaign-demo: Matches nutzen ausschließlich Ambience
  startCampaignDemoMusic() {
    this.stopMusic();
    this.playBattlefieldAmbience();
  }

  // Hard cut (not a fade) - docs/about music folder.rtf calls for a cut
  // specifically here, not the usual crossfade. Safe to call unconditionally
  // from wherever the menu/hub screen opens (js/ui/deckBuilder.js's
  // openModal()) without checking what the player was doing beforehand -
  // except when menu music is already the active context (e.g. reopening
  // Archive while its own music plays), where cutting would wipe this.bgm/
  // _musicContext right before playMenuMusic()'s idempotency check runs,
  // forcing an unwanted restart of the loop.
  stopCampaignDemoMusicHardCut() {
    if (this._musicContext === 'menu') return;
    this._stopMusicHardCut();
    this._musicContext = null;
  }

  // Prepared, not yet wired to anything: no Achievement or XP feature
  // exists in the codebase yet to call these (only docs/XP_PROGRESSION_
  // PLAN.md describes one) - ready for whenever that ships (docs/
  // SOUND_BATCH_PLAN.md section 5.2). onReveal fires at 7.1s into the
  // track, matching docs/about music folder.rtf's timing note: the first
  // 7.1s are the build-up, the achievement UI should only reveal itself
  // once the audio's payoff moment lands, even though the unlock itself
  // already happened earlier.
  /**
   * Helper to resolve audio file paths across assets/music and assets/sound.
   */
  _resolveSoundFilePath(file, defaultBase = MUSIC_BASE) {
    if (!file) return '';
    if (file.startsWith('http://') || file.startsWith('https://')) return file;
    if (file.startsWith('/assets/')) return file;
    if (file.startsWith('assets/')) return file;
    if (file.startsWith('music/') || file.startsWith('short/') || file.startsWith('OST/') || file.startsWith('var/') || (file.startsWith('map/') && file.endsWith('.mp3'))) {
      return 'assets/music/' + file.replace(/^music\//, '');
    }
    if (file.startsWith('sound/')) {
      return 'assets/sound/' + file.replace(/^sound\//, '');
    }
    return 'assets/sound/' + file;
  }

  /**
   * Plays a dedicated pop-up / toast fanfare from SOUND_REGISTRY
   * (e.g. popups.achievement, popups.daily_quest, popups.daily_jackpot, popups.level_up).
   * Supports individual sound assignment and volume sliders in Sound Review.
   *
   * Uses Howler (WebAudio) when available to prevent browser autoplay blocking,
   * falls back to HTML5 Audio element.
   *
   * @param {string|Function} typeOrOnReveal - Pop-up type ('achievement'|'daily_quest'|'daily_jackpot'|'level_up') or onReveal callback
   * @param {Function} [onReveal] - Optional callback fired at the reveal moment
   * @returns {HTMLAudioElement|Object}
   */
  playPopupFanfare(typeOrOnReveal = 'achievement', onReveal = null) {
    let type = typeOrOnReveal;
    let revealCb = onReveal;
    if (typeof typeOrOnReveal === 'function') {
      type = 'achievement';
      revealCb = typeOrOnReveal;
    }
    if (!type || typeof type !== 'string') type = 'achievement';

    // Look up in registry: popups[type] -> announcer[type] -> fallback
    const popupDef = this.registry?.popups?.[type] || this.registry?.announcer?.[type];
    let file = null;
    let volume = 0.9;

    if (popupDef && Array.isArray(popupDef.files) && popupDef.files.length > 0) {
      file = this._pickFromPool(popupDef.files, `popups.${type}`);
      if (typeof popupDef.volume === 'number') volume = popupDef.volume;
    }

    if (!file) {
      file = (type === 'level_up') ? 'short/xp_level_up.mp3' : 'short/achievement.mp3';
    }

    // Determine lead-in timing: only achievement.mp3 has a 7.1s build-up
    const isAchievementTrack = (file === 'short/achievement.mp3' || file.endsWith('achievement.mp3'));
    const leadInMs = isAchievementTrack ? 7100 : 0;

    if (revealCb) {
      if (leadInMs > 0) {
        setTimeout(revealCb, leadInMs);
      } else {
        revealCb();
      }
    }

    if (this.isMuted) {
      return { file, volume: 0, leadInMs, played: false };
    }

    // Master volume: active sound level (supports either SFX or Music volume setting)
    const sfxVol = (typeof this.sfxVolume === 'number') ? this.sfxVolume : 1.0;
    const musicVol = (typeof this.musicVolume === 'number') ? this.musicVolume : 1.0;
    const effectiveMasterVol = Math.max(sfxVol, musicVol);
    const finalVolume = Math.max(0, Math.min(1.0, effectiveMasterVol * volume));

    // 1. Try Howler first (WebAudio context is unlocked by user interactions - avoids mobile/desktop autoplay blocks)
    if (typeof window !== 'undefined' && window.Howl) {
      try {
        this._resumeContextIfNeeded();
        const soundId = this.playSample(file, {
          volume: volume,
          category: 'ui',
          throttleMs: 0
        });
        return { file, volume: finalVolume, leadInMs, soundId, isHowler: true, played: true };
      } catch (err) {
        console.warn('[SoundEngine] Howler playPopupFanfare failed, falling back to Audio:', err);
      }
    }

    // 2. HTML5 Audio element fallback (Node test environment or plain Audio fallback)
    if (typeof Audio !== 'undefined') {
      try {
        const src = this._resolveSoundFilePath(file, MUSIC_BASE);
        const encodedSrc = encodeURI(decodeURI(src));
        const el = new Audio(encodedSrc);
        el.volume = finalVolume;
        el.leadInMs = leadInMs;
        el.play().catch(err => {
          console.warn('[SoundEngine] playPopupFanfare Audio playback was rejected:', err);
        });
        return el;
      } catch (err) {
        console.warn('[SoundEngine] playPopupFanfare Audio creation failed:', err);
      }
    }

    return { file, volume: finalVolume, leadInMs, played: false };
  }

  // Achievement Fanfare — delegates to playPopupFanfare for registry-backed sound & volume configuration
  playAchievementFanfare(typeOrOnReveal = null, onReveal = null) {
    if (typeof typeOrOnReveal === 'string') {
      return this.playPopupFanfare(typeOrOnReveal, onReveal);
    }
    return this.playPopupFanfare('achievement', typeOrOnReveal || onReveal);
  }

  // Daily Quest Fanfare — green toast notification
  playQuestFanfare(onReveal = null) {
    return this.playPopupFanfare('daily_quest', onReveal);
  }

  // Daily Jackpot Fanfare — gold toast notification
  playDailyJackpotFanfare(onReveal = null) {
    return this.playPopupFanfare('daily_jackpot', onReveal);
  }

  playXpBoost() {
    if (typeof window === 'undefined' || this.isMuted) return null;
    const regDef = this.registry?.announcer?.xp_boost;
    let file = 'short/xp_boost.mp3';
    let volMult = 0.85;
    if (regDef && Array.isArray(regDef.files) && regDef.files.length > 0) {
      file = this._pickFromPool(regDef.files, 'announcer.xp_boost');
      if (typeof regDef.volume === 'number') volMult = regDef.volume;
    }

    const sfxVol = (typeof this.sfxVolume === 'number') ? this.sfxVolume : 1.0;
    const musicVol = (typeof this.musicVolume === 'number') ? this.musicVolume : 1.0;
    const masterVol = Math.max(sfxVol, musicVol);
    const finalVolume = Math.max(0, Math.min(1.0, masterVol * volMult));

    if (typeof window !== 'undefined' && window.Howl) {
      try {
        this._resumeContextIfNeeded();
        return this.playSample(file, { volume: volMult, category: 'ui', throttleMs: 0 });
      } catch {}
    }

    if (typeof Audio !== 'undefined') {
      const src = this._resolveSoundFilePath(file, MUSIC_BASE);
      const el = new Audio(encodeURI(decodeURI(src)));
      el.volume = finalVolume;
      el.play().catch(() => {});
      return el;
    }
    return null;
  }

  playXpLevelUp(force = false) {
    if (typeof window === 'undefined' || this.isMuted) return null;
    const now = performance.now();
    // Throttle level-up fanfares to avoid overlapping audio tracks (5s cooldown)
    if (!force && this._lastXpLevelUpTime && (now - this._lastXpLevelUpTime) < 5000) {
      return this._xpLevelUpAudio || null;
    }
    this._lastXpLevelUpTime = now;
    if (this._xpLevelUpAudio) {
      try {
        if (typeof this._xpLevelUpAudio.pause === 'function') {
          this._xpLevelUpAudio.pause();
          this._xpLevelUpAudio.currentTime = 0;
        } else if (typeof this._xpLevelUpAudio.stop === 'function') {
          this._xpLevelUpAudio.stop();
        }
      } catch {}
    }

    const regDef = this.registry?.popups?.level_up || this.registry?.announcer?.level_up;
    let file = 'short/xp_level_up.mp3';
    let volMult = 0.9;
    if (regDef && Array.isArray(regDef.files) && regDef.files.length > 0) {
      file = this._pickFromPool(regDef.files, 'popups.level_up');
      if (typeof regDef.volume === 'number') volMult = regDef.volume;
    }

    const sfxVol = (typeof this.sfxVolume === 'number') ? this.sfxVolume : 1.0;
    const musicVol = (typeof this.musicVolume === 'number') ? this.musicVolume : 1.0;
    const masterVol = Math.max(sfxVol, musicVol);
    const finalVolume = Math.max(0, Math.min(1.0, masterVol * volMult));

    if (typeof window !== 'undefined' && window.Howl) {
      try {
        this._resumeContextIfNeeded();
        const soundId = this.playSample(file, { volume: volMult, category: 'ui', throttleMs: 0 });
        this._xpLevelUpAudio = { soundId, stop: () => this.stopSample?.(file, soundId) };
        return this._xpLevelUpAudio;
      } catch {}
    }

    if (typeof Audio !== 'undefined') {
      const src = this._resolveSoundFilePath(file, MUSIC_BASE);
      const el = new Audio(encodeURI(decodeURI(src)));
      el.volume = finalVolume;
      this._xpLevelUpAudio = el;
      el.play().catch(() => {});
      return el;
    }
    return null;
  }

  // ---------------------------------------------------------------------
  // Sample layer for the SFX-Batch-Pipeline (docs/SOUND_BATCH_PLAN.md
  // section 3.3) - built on the vendored Howler (js/vendor/howler.js,
  // window.Howl/window.Howler), NOT the plain-<audio> Music Director above:
  // combat/UI events need concurrent-voice pooling and iOS unlock handling
  // that Howler already solves, in a way many-short-samples benefits from
  // and single long-form music tracks don't. Infrastructure only for now -
  // no existing play*() method below has been switched over to it yet,
  // that happens per-event once Batch 1+ picks a candidate through
  // tools/sound-review/.
  // ---------------------------------------------------------------------

  _getSample(file, opts = {}) {
    if (typeof window === 'undefined' || !window.Howl) return null;
    this._samples ??= new Map();
    if (!this._samples.has(file)) {
      const srcPath = file.startsWith('assets/')
        ? file
        : (file.startsWith('music/') || file.startsWith('OST/') || file.startsWith('short/') || file.startsWith('var/') || (file.startsWith('map/') && file.endsWith('.mp3')))
          ? `assets/music/${file.replace(/^music\//, '')}`
          : `assets/sound/${file}`;
      const howl = new window.Howl({ src: [encodeURI(decodeURI(srcPath))], ...opts });
      this._samples.set(file, howl);
      // Its decoded size is only known once loaded, so that is when the cache may have to shrink.
      howl.once?.('load', () => this.trimSampleCache());
    }
    (this._sampleUsedAt ??= new Map()).set(file, performance.now());
    return this._samples.get(file);
  }

  // Keeps a file's Howl (and its decoded buffer) out of trimSampleCache().
  _pinSamples(files) {
    this._pinnedSamples ??= new Set();
    for (const f of files) this._pinnedSamples.add(f);
  }

  // _samples used to grow for the whole session: every distinct SFX ever played stayed decoded
  // (~350 KB per second of audio) until the page died, which on iPhone ends in a jetsam kill
  // (see js/ui/memProbe.js). Least-recently-used samples that are not playing, pinned or held by a
  // field are unloaded once the estimated decoded size passes limitMB; the next play just re-loads
  // it from the HTTP cache. Returns the number of unloaded samples.
  trimSampleCache(limitMB = 48) {
    const samples = this._samples;
    if (!samples || samples.size === 0 || typeof window === 'undefined') return 0;
    const bytesPerSec = (window.Howler?.ctx?.sampleRate || 44100) * 2 * 4; // same estimate as memProbe
    let total = 0;
    const entries = [];
    for (const [file, howl] of samples) {
      const dur = howl.state?.() === 'loaded' && howl._webAudio ? (howl.duration?.() || 0) : 0;
      const bytes = dur * bytesPerSec;
      total += bytes;
      entries.push({ file, howl, bytes, used: this._sampleUsedAt?.get(file) || 0 });
    }
    const limit = limitMB * 1048576;
    if (total <= limit) return 0;
    const target = limit * 0.75;
    let unloaded = 0;
    entries.sort((a, b) => a.used - b.used);
    for (const e of entries) {
      if (total <= target) break;
      if (e.bytes <= 0) continue;
      if (this._pinnedSamples?.has(e.file)) continue;
      if (e.howl === this._wheelieDriveSample || e.howl === this._casinoSpinSample) continue;
      if (e.howl.playing?.()) continue;
      try { e.howl.unload(); } catch {}
      samples.delete(e.file);
      this._sampleUsedAt?.delete(e.file);
      total -= e.bytes;
      unloaded++;
    }
    return unloaded;
  }

  // Units whose own SFX are not part of preloadCommonSamples(): the first
  // time such a sound plays, _getSample() would fetch + decode it right then,
  // mid-combat. HudManager calls warmUnitSamples(card.name) whenever a card
  // enters the hand, so the files are already decoded by the time the unit
  // is actually played (online and offline alike). Idempotent, one Set check
  // per call. The Hacker files are the short trims of the original long
  // sci-fi stingers (hacker_*.m4a) - see docs/PERFORMANCE_AUDIT_PLAN_2026-10-23.md.
  warmUnitSamples(cardName) {
    const files = UNIT_WARM_SAMPLES[cardName];
    if (!files || this.isMuted || typeof window === 'undefined' || !window.Howl) return;
    this._warmedUnits ??= new Set();
    if (this._warmedUnits.has(cardName)) return;
    this._warmedUnits.add(cardName);
    this._pinSamples(files);
    for (const f of files) this._getSample(f);
  }

  // Releases unit-specific audio samples pinned during previous matches.
  // Unpins them from _pinnedSamples and clears _warmedUnits so that trimSampleCache()
  // can reclaim stale decoded PCM buffers without evicting common UI sounds.
  releaseMatchWarmSamples() {
    this._warmedUnits?.clear?.();
    if (this._pinnedSamples) {
      for (const list of Object.values(UNIT_WARM_SAMPLES)) {
        if (Array.isArray(list)) {
          for (const f of list) {
            this._pinnedSamples.delete(f);
          }
        }
      }
    }
    return this.trimSampleCache(12);
  }

  // Cargo Bay pack tear: every beat (lock-in, charge, arcs, snap, detonation, flips) is a file that
  // would otherwise be constructed - fetched and decoded - at the very beat it should be heard.
  // Called when the Cargo Bay opens, so that happens while the player is still picking a pack.
  preloadCargoBaySamples() {
    if (this.isMuted || typeof window === 'undefined' || !window.Howl) return 0;
    const files = new Set(['baked/crate_charge.m4a', 'lowFrequency_explosion_000.ogg', 'more/impacts_and_crashes/air_pressure_blast_1.m4a']);
    const walk = (o) => {
      if (!o || typeof o !== 'object') return;
      if (Array.isArray(o.files)) o.files.forEach((f) => typeof f === 'string' && files.add(f));
      for (const v of Object.values(o)) if (v && typeof v === 'object' && !Array.isArray(v)) walk(v);
    };
    walk(this.registry?.cargo_bay);
    this._pinSamples(files);
    for (const f of files) this._getSample(f);
    return files.size;
  }

  addLogListener(fn) {
    this._audioLogListeners ??= [];
    this._audioLogListeners.push(fn);
  }

  removeLogListener(fn) {
    if (!this._audioLogListeners) return;
    this._audioLogListeners = this._audioLogListeners.filter((l) => l !== fn);
  }

  _notifyLog(file, opts) {
    if (!this._audioLogListeners || this._audioLogListeners.length === 0) return;
    for (const fn of this._audioLogListeners) {
      try { fn({ file, timestamp: Date.now(), opts }); } catch {}
    }
  }

  // `durationMs` truncates playback early via Howl.stop() - für längere
  // Quelldateien, falls nicht vorab gebacken.
  // `randomPitch` wendet subtile Tonhöhen-Schwankungen (Anti-Fatigue) an.
  // `randomVolume` wendet subtilen Lautstärke-Jitter an.
  // `throttleMs` verhindert Clipping bei identischen Massen-Triggern.
  // `pan` / `x` positioniert das Sample im Stereo-Panorama (-0.75 bis +0.75).
  // `category` ordnet das Sample einer Voice-Kategorie für Priorisierung zu.
  playSample(file, {
    volume = 1,
    durationMs = null,
    loop = false,
    rate = null,
    randomPitch = false,
    pitchRange = 0.08,
    randomVolume = false,
    volumeRange = 0.10,
    throttleMs = 25,
    pan = null,
    x = null,
    category = 'default'
  } = {}) {
    if (this.isMuted || this.sfxVolume <= 0) return null;
    this._resumeContextIfNeeded();

    if (throttleMs > 0) {
      const now = performance.now();
      this._throttleMap ??= new Map();
      const last = this._throttleMap.get(file) || 0;
      if (now - last < throttleMs) return;
      this._throttleMap.set(file, now);
    }

    const sample = this._getSample(file);
    if (!sample) return;
    this._enforceVoiceCap(category);

    if (x !== null && typeof x === 'number' && pan === null) {
      pan = this.panFromX(x);
    }

    let effVol = volume;
    if (randomVolume) {
      effVol = effVol * (1 - volumeRange / 2 + Math.random() * volumeRange);
    }
    if (this._combatDucking && category === 'combat_infantry') {
      effVol *= (this._combatDuckFactor || 0.6);
    }
    const finalVol = Math.max(0, Math.min(1, effVol * this.sfxVolume));

    sample.volume(finalVol);
    const id = sample.play();
    if (id != null) {
      sample.volume(finalVol, id);
      if (typeof rate === 'number') {
        sample.rate(rate, id);
      } else if (randomPitch) {
        const r = (1 - pitchRange / 2) + Math.random() * pitchRange;
        sample.rate(r, id);
      }
      if (pan !== null && typeof sample.stereo === 'function') {
        sample.stereo(pan, id);
      }
      if (loop && typeof sample.loop === 'function') {
        sample.loop(true, id);
      }
      this._trackVoice(sample, id, category, volume);
      if (durationMs) setTimeout(() => {
        try { sample.stop(id); } catch {}
      }, durationMs);
    }
    this._notifyLog(file, { volume: finalVol, baseVolume: volume, randomPitch, randomVolume, pan, category });
    return id;
  }

  // Category-basiertes Voice-Limiting & Anti-Kollision (Maßnahme 3)
  // Verhindert Soundbrei und stellt sicher, dass Infanteriefeuer niemals
  // schwere Ereignisse, Nuke-Alarme oder Barks aus den Audiokanälen drängt.
  static MAX_CONCURRENT_VOICES = 10;

  _trackVoice(sample, id, category = 'default', baseVolume = 1) {
    this._activeVoices ??= [];
    const entry = { sample, id, category, baseVolume };
    this._activeVoices.push(entry);
    const untrack = () => {
      const idx = this._activeVoices.indexOf(entry);
      if (idx !== -1) this._activeVoices.splice(idx, 1);
    };
    if (typeof sample.once === 'function') {
      sample.once('end', untrack, id);
      sample.once('stop', untrack, id);
    }
  }

  _enforceVoiceCap(category = 'default') {
    this._activeVoices ??= [];
    const cap = CATEGORY_VOICE_CAPS[category] ?? CATEGORY_VOICE_CAPS.default ?? 4;

    // 1. Category Voice Stealing: Älteste Stimme derselben Kategorie stehlen (zero-allocation)
    let catCount = 0;
    for (let i = 0; i < this._activeVoices.length; i++) {
      if (this._activeVoices[i].category === category) catCount++;
    }

    while (catCount >= cap) {
      let oldestIdx = -1;
      for (let i = 0; i < this._activeVoices.length; i++) {
        if (this._activeVoices[i].category === category) {
          oldestIdx = i;
          break;
        }
      }
      if (oldestIdx === -1) break;
      const oldest = this._activeVoices.splice(oldestIdx, 1)[0];
      catCount--;
      try {
        oldest.sample.stop(oldest.id);
      } catch {}
    }

    // 2. Globales Stimmenlimit
    while (this._activeVoices.length >= SoundEngine.MAX_CONCURRENT_VOICES) {
      const oldest = this._activeVoices.shift();
      try {
        oldest.sample.stop(oldest.id);
      } catch {}
    }
  }

  // Anti-repetition pool picker (avoids picking the exact same sound consecutively if >1 items)
  _pickFromPool(files, poolKey = null) {
    if (!files || files.length === 0) return null;
    if (files.length === 1) return files[0];
    this._lastPickedInPool ??= new Map();
    const last = poolKey ? this._lastPickedInPool.get(poolKey) : null;
    let candidates = files;
    if (last && files.length > 1) {
      candidates = files.filter(f => f !== last);
      if (candidates.length === 0) candidates = files;
    }
    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    if (poolKey) this._lastPickedInPool.set(poolKey, picked);
    return picked;
  }

  playPooled(files, opts) {
    if (!files || files.length === 0) return;
    const file = this._pickFromPool(files);
    if (file) this.playSample(file, opts);
  }

  // Play a sound configured in SOUND_REGISTRY for a specific unit
  playUnitSound(unitType, eventName, opts = {}) {
    if (this.isMuted) return null;
    const unitKey = unitType || opts.unitType;
    const normalizedKey = typeof unitKey === 'string' ? unitKey.replace(/\s+/g, '') : unitKey;
    const unitDef = this.registry?.units?.[unitKey] || this.registry?.units?.[normalizedKey];
    const eventDef = unitDef?.[eventName];

    if (eventDef && Array.isArray(eventDef.files) && eventDef.files.length > 0) {
      const file = this._pickFromPool(eventDef.files, `${normalizedKey || unitKey}.${eventName}`);
      if (file) {
        return this.playSample(file, {
          volume: eventDef.volume ?? 1,
          randomPitch: eventDef.randomPitch ?? true,
          pitchRange: eventDef.pitchRange ?? 0.06,
          randomVolume: eventDef.randomVolume ?? true,
          category: eventName === 'attack' ? 'combat_infantry' : (eventName === 'spawn' || eventName === 'death' ? 'barks' : 'default'),
          ...opts
        });
      }
    }

    // Fallbacks if no files configured in registry for this unit event
    if (eventName === 'spawn') {
      const entry = UNIT_SPAWN_FILES[unitKey];
      if (entry) {
        const file = typeof entry === 'object' && entry !== null ? entry.file : entry;
        const defaultVol = typeof entry === 'object' && entry !== null && typeof entry.volume === 'number' ? entry.volume : 1;
        return this.playSample(file, { category: 'barks', volume: defaultVol, ...opts });
      }
      const race = unitDef?.race || opts.race || 'Human';
      return this.playBark(race, 'spawn', opts);
    }

    if (eventName === 'death') {
      const race = unitDef?.race || opts.race || 'Human';
      this.playBark(race, 'death', opts);
      const isMachinery = unitDef?.isMachinery ?? opts.isMachinery;
      return isMachinery ? this.playUnitDeathMachinery(opts) : this.playUnitDeathInfantry(opts);
    }

    return null;
  }

  // Play a sound configured in SOUND_REGISTRY for any category (spells, buildings, ui, announcer, etc.)
  playRegistrySound(category, entityId, eventId = 'fire', opts = {}) {
    if (this.isMuted || this.sfxVolume <= 0) return null;
    const cat = this.registry?.[category];
    const entity = cat?.[entityId];
    const eventDef = (entity && Array.isArray(entity.files)) ? entity : (entity?.[eventId] || entity);

    if (eventDef && Array.isArray(eventDef.files) && eventDef.files.length > 0) {
      const file = opts.file || this._pickFromPool(eventDef.files, `${category}.${entityId}.${eventId}`);
      if (file) {
        const defaultCategory = (category === 'combat_global' || category === 'buildings')
          ? 'combat_heavy'
          : (category === 'ui' || category === 'announcer' ? 'ui' : (category === 'spells' ? 'special' : 'default'));
        return this.playSample(file, {
          volume: eventDef.volume ?? 1,
          durationMs: eventDef.durationMs ?? null,
          randomPitch: eventDef.randomPitch ?? false,
          pitchRange: eventDef.pitchRange ?? 0.08,
          category: defaultCategory,
          ...opts
        });
      }
    }
    return null;
  }

  updateRegistry(newRegistry) {
    if (newRegistry) {
      this.registry = newRegistry;
      if (this._musicContext === 'menu') {
        this._applyLiveMusicVolume();
      }
    }
  }

  // Spielt mehrere Dateien gleichzeitig ab (Layering) statt nacheinander -
  // für Momente, die sich klar größer/dramatischer anfühlen müssen als ein
  // einzelner Sample es hergibt (docs/SOUND_BATCH_PLAN.md section 4.2,
  // `explosion_massive`). `volumes` erlaubt pro Layer eine eigene Lautstärke
  // (z. B. den Hauptlayer voll, einen zweiten Layer leiser drunterlegen).
  playLayered(files, volumes = [], opts = {}) {
    if (typeof volumes === 'object' && !Array.isArray(volumes) && volumes !== null) {
      opts = volumes;
      volumes = [];
    }
    files.forEach((file, i) => this.playSample(file, { volume: volumes[i] ?? 1, ...opts }));
  }

  // Dauerhafter Loop-Layer (Batch 4, docs/SOUND_BATCH_PLAN.md section 4.4)
  // - eigene Howl-Instanz statt der geteilten _getSample()-SFX-Cache, damit
  // `loop: true` nicht versehentlich auf einen anderswo als One-Shot
  // genutzten Sample "vererbt" wird. Gleicher Schalter wie Musik
  // (musicEnabled) - Atmosphäre ist Teil von "SFX+Music", nicht "SFX only".
  // Idempotent für dieselbe Datei (erneutes Aufrufen mit demselben Kontext
  // startet den Loop nicht neu).
  playAmbience(file) {
    if (typeof window === 'undefined' || !window.Howl) return;
    if (this._ambienceFile === file) return;
    this.stopAmbience();
    const tension = this._matchTension || 0;
    const initialVol = (0.28 + tension * 0.14) * this.sfxVolume;
    const initialRate = 1.0 + tension * 0.03;
    this._ambience = new CrossfadeLoop(`assets/sound/${file}`, { volume: initialVol, rate: initialRate, fadeMs: 1500 });
    this._ambienceFile = file;
    if (!this.isMuted && this.musicEnabled) this._ambience.play();
  }

  stopAmbience() {
    this._ambiencePausedByVisibility = false;
    this._ambience?.stop();
    this._ambience?.unload?.();
    this._ambience = null;
    this._ambienceFile = null;
    this.stopWeather();
  }

  // Synthesized Sci-Fi UI Tone (used by navigation and interface chimes)
  playTone(freq = 750, duration = 0.06) {
    if (this.isMuted || this.sfxVolume <= 0) return;
    if (!this.initialized) this.init();
    if (!this.ctx) return;
    try {
      const t = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.035 * this.sfxVolume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(t);
      osc.stop(t + duration);
    } catch (e) {
      // Audio fallback silent
    }
  }

  // Synthesized Sci-Fi SFX
  // File-based seit Batch 2 (docs/SOUND_BATCH_PLAN.md section 4.2) -
  // Debounce-Fenster bleibt wie in der Synthese-Version, damit sehr
  // schnelle Wiederholtreffer (mehrere Einheiten im selben Tick) nicht zur
  // Sample-Maschinengewehr werden. `performance.now()` statt
  // `ctx.currentTime`, weil playPooled() keinen AudioContext mehr braucht.
  playLaser(type = 'normal', opts = {}) {
    if (typeof type === 'object' && type !== null) {
      opts = type;
      type = opts.type || 'normal';
    }
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastLaserTime && (t - this._lastLaserTime) < 40) return;
    this._lastLaserTime = t;
    this.playPooled(type === 'heavy' ? LASER_HEAVY_FILES : LASER_NORMAL_FILES, {
      randomPitch: true,
      pitchRange: 0.08,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_infantry',
      ...opts
    });
  }

  playGauss(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastGaussTime && (t - this._lastGaussTime) < 45) return;
    this._lastGaussTime = t;

    const unitKey = (opts.unitType || 'Marine').replace(/\s+/g, '');
    const regAttack = this.registry?.units?.[unitKey]?.attack || this.registry?.units?.Marine?.attack;
    if (regAttack && Array.isArray(regAttack.files) && regAttack.files.length > 0) {
      const file = this._pickFromPool(regAttack.files, `units.${unitKey}.attack`);
      if (file) {
        return this.playSample(file, {
          volume: regAttack.volume ?? 0.7,
          randomPitch: regAttack.randomPitch ?? true,
          pitchRange: regAttack.pitchRange ?? 0.08,
          randomVolume: true,
          volumeRange: 0.10,
          category: 'combat_infantry',
          ...opts
        });
      }
    }

    this.playSample('more/foley/body_hit_02.m4a', {
      volume: 0.7,
      randomPitch: true,
      pitchRange: 0.08,
      randomVolume: true,
      volumeRange: 0.10,
      category: 'combat_infantry',
      ...opts
    });
  }

  // A9 Piranha Dual-Wing-Autocannon-Schuss. Teilte sich bisher playGauss()
  // mit Marine & Co. (js/engine/unit.js dualWeapon-Zweig, js/net/
  // stateStream.js) - eigener, leiserer und tiefer gepitchter Sound auf
  // Nutzer-Feedback ("20% leiser, ein wenig runterpitchen").
  playA9PiranhaShot(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastA9PiranhaShotTime && (t - this._lastA9PiranhaShotTime) < 45) return;
    this._lastA9PiranhaShotTime = t;
    this.playSample('more/foley/body_hit_03.m4a', {
      volume: 0.64, // playGauss() 0.8 * 0.8 (20% leiser)
      rate: 0.86 + Math.random() * 0.08, // playGauss()'s 0.96-1.04-Zufallspitch tiefer verschoben
      randomVolume: true,
      volumeRange: 0.10,
      category: 'combat_infantry',
      ...opts
    });
  }

  // A9 Grizzly Autocannon-Schuss (Aufklärungs-Autokanone, SOUND_REGISTRY.units.A9Grizzly.attack)
  playA9GrizzlyShot(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastA9GrizzlyShotTime && (t - this._lastA9GrizzlyShotTime) < 45) return null;
    this._lastA9GrizzlyShotTime = t;

    const res = this.playUnitSound ? this.playUnitSound('A9Grizzly', 'attack', opts) : null;
    if (res !== null && res !== undefined) return res;
    const regRes = this.playRegistrySound('units', 'A9Grizzly', 'attack', { category: 'combat_infantry', ...opts });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playGauss({ unitType: 'A9Grizzly', ...opts });
  }

  // A9 Pigeon 30mm Chin Gatling-Schuss (0.2s Kadenz)
  playA9PigeonShot(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastA9PigeonShotTime && (t - this._lastA9PigeonShotTime) < 42) return null;
    this._lastA9PigeonShotTime = t;

    const regRes = this.playRegistrySound('units', 'A9Pigeon', 'attack', {
      rate: 0.92 + Math.random() * 0.12,
      category: 'combat_infantry',
      ...opts
    });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playSample('more/foley/body_hit_03.m4a', {
      volume: 0.58,
      rate: 0.92 + Math.random() * 0.12,
      randomVolume: true,
      volumeRange: 0.10,
      category: 'combat_infantry',
      ...opts
    });
  }

  // A9 Pigeon: Schwerer Bombenabwurf im Hover über Bodenziel (Pylon-Ausklinken + pfeifende Sturzbombe)
  playA9PigeonBombDrop(opts = {}) {
    if (this.isMuted) return null;
    // Procedural descending bomb whistle (2200 Hz -> 580 Hz over 0.55s)
    try {
      if (this.ctx && this.ctx.state === 'running') {
        const t = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(2200, t);
        osc.frequency.exponentialRampToValueAtTime(580, t + 0.52);
        const masterVol = (this.sfxVolume !== undefined ? this.sfxVolume : 0.7) * (opts.volume !== undefined ? opts.volume : 0.65);
        gain.gain.setValueAtTime(0.001, t);
        gain.gain.linearRampToValueAtTime(0.08 * masterVol, t + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
        osc.connect(gain);
        gain.connect(this.sfxGain || this.ctx.destination);
        osc.start(t);
        osc.stop(t + 0.55);
      }
    } catch (_) {}

    const regRes = this.playRegistrySound('units', 'A9Pigeon', 'bombDrop', { category: 'combat_heavy', ...opts });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playSample('more/motions_and_transitions/air_reverse_burst_2.m4a', {
      volume: 0.7,
      randomPitch: true,
      pitchRange: 0.06,
      category: 'combat_heavy',
      ...opts
    });
  }

  // A9 Pigeon: 100-Damage-Splash-Explosion der Bodenbombe
  playA9PigeonBombExplosion(opts = {}) {
    if (this.isMuted) return null;
    const regRes = this.playRegistrySound('units', 'A9Pigeon', 'bombExplosion', { category: 'combat_heavy', ...opts });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playSample('more/explosions/explosion_5.m4a', {
      volume: 0.85,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'combat_heavy',
      ...opts
    });
  }

  // A9 Pigeon: Tod (Maschinen-Explosion + auffliegender Taubenschwarm + Gurren)
  playA9PigeonDeath(opts = {}) {
    if (this.isMuted) return null;
    this.playSample('more/explosions/explosion_4.m4a', {
      volume: 0.55,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'combat_heavy',
      ...opts
    });
    this.playSample('more/animals/bird_flying.m4a', {
      volume: 0.85,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'barks',
      ...opts
    });
    return this.playSample('more/animals/bird_dove.m4a', {
      volume: 0.75,
      randomPitch: true,
      pitchRange: 0.06,
      category: 'barks',
      ...opts
    });
  }

  // A9 Piranha: durchgehendes Rotor-/Motor-Loop, sobald mindestens ein A9
  // Piranha lebt (Nutzer-Feedback: "direkt ab dem Ausspielen, solange er am
  // Leben ist"). Referenzgezählt wie playFlamethrower() unten - ein
  // gemeinsamer Loop statt einem Howl pro Einheit, damit mehrere gleichzeitig
  // lebende Piranhas sich nicht gegenseitig stumm schalten (start/stop-Aufrufe
  // je Einheit in js/engine/unit.js's update()/die()/takeDamage()).
  // Quelle: 200ms-1000ms aus more/mechtech/motor_2.m4a getrimmt und geloopt
  // (tools/bake-trimmed-audio.mjs -> assets/sound/baked/a9piranha_motor_loop.m4a,
  // mit winzigem In/Out-Microfade für einen klickfreien Loop-Nahtübergang).
  // Der hörbare "Fade in" beim ersten Start kommt bewusst NICHT aus der
  // Datei (das würde bei jedem Loop-Durchlauf erneut absacken), sondern aus
  // dem Howler .fade() unten - läuft nur einmal, beim tatsächlichen Start.
  startA9PiranhaMotorLoop() {
    this._a9MotorRefCount = (this._a9MotorRefCount || 0) + 1;
    if (this.isMuted) return;
    if (this._a9MotorRefCount > 1) return; // Loop läuft schon für eine andere lebende Piranha
    if (typeof window === 'undefined' || !window.Howl) return;
    if (!this._a9MotorHowl) {
      this._a9MotorHowl = new window.Howl({
        src: ['assets/sound/baked/a9piranha_motor_loop.m4a'],
        loop: true,
        volume: 0
      });
    }
    const targetVol = Math.max(0, Math.min(1, 0.1 * this.sfxVolume));
    this._a9MotorHowl.volume(0);
    this._a9MotorHowl.play();
    this._a9MotorHowl.fade(0, targetVol, 500);
  }

  stopA9PiranhaMotorLoop() {
    this._a9MotorRefCount = Math.max(0, (this._a9MotorRefCount || 0) - 1);
    if (this._a9MotorRefCount > 0) return; // eine andere Piranha lebt noch
    if (!this._a9MotorHowl) return;
    try {
      this._a9MotorHowl.fade(this._a9MotorHowl.volume(), 0, 200);
      setTimeout(() => { try { this._a9MotorHowl?.pause(); } catch {} }, 210);
    } catch {}
  }

  // Sentinal Lock bio-glob spit / spit shot. Subtiler, organischer Hauch / Swish
  // anstelle des bisherigen lauten, squelchigen slime_000.ogg.
  playSentinalSpit(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastSentinalSpitTime && (t - this._lastSentinalSpitTime) < 50) return;
    this._lastSentinalSpitTime = t;
    this.playSample('more/motions_and_transitions/swish_1.m4a', {
      volume: 0.09, // 40% leiser auf Nutzer-Feedback (war 0.15)
      randomPitch: true,
      pitchRange: 0.06,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_infantry',
      ...opts
    });
  }

  // File-based seit Batch 2 (docs/SOUND_BATCH_PLAN.md section 4.2).
  // Delegiert an playSentinalSpit() für volle Abwärtskompatibilität.
  playHiss(opts = {}) {
    this.playSentinalSpit(opts);
  }

  // Sul Preserver Lock: periodic beam pulse. File-based seit Batch 2 (docs/
  // SOUND_BATCH_PLAN.md section 4.2) - Debounce-Fenster (0.35s) unverändert
  // aus der Synthese-Version übernommen: Called from Unit.
  // resolveNewCombatAttack()'s beam-damage flush (~every 0.4s while
  // locked), das Fenster verhindert einen Doppel-Trigger im selben Flush.
  // Nutzt das gebackene baked/preserver_beam.m4a (400ms, aus electrical_noise_3.m4a)
  // mit hartem 400ms-Cap, damit der Sound sauber aufhört sobald der Beam abreißt.
  playPreserverBeamPulse(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastPreserverBeamTime && (t - this._lastPreserverBeamTime) < 350) return null;
    this._lastPreserverBeamTime = t;
    const res = this.playRegistrySound('units', 'PreserverLock', 'attack', {
      durationMs: 400,
      category: 'combat_infantry',
      ...opts
    });
    if (res !== null) return res;
    return this.playSample('baked/preserver_beam.m4a', {
      volume: 0.25,
      durationMs: 400,
      category: 'combat_infantry',
      ...opts
    });
  }

  // Sul Bastion Lock: Hot molten metal bullets bursting on impact (10 shots/s).
  // Throttled to 75ms to avoid sound pileup at high cadence while maintaining punchy feedback.
  playBastionShot(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastBastionShotTime && (t - this._lastBastionShotTime) < 75) return null;
    this._lastBastionShotTime = t;
    const res = this.playRegistrySound('units', 'BastionLock', 'attack', {
      category: 'combat_heavy',
      ...opts
    });
    if (res !== null) return res;
    return this.playSample('impactPlate_heavy_001.ogg', {
      volume: 0.18,
      randomPitch: true,
      pitchRange: 0.12,
      randomVolume: true,
      volumeRange: 0.06,
      category: 'combat_heavy',
      ...opts
    });
  }

  // Sul Bastion Lock: Heavy organic shield slam into the ground upon deploying.
  playBastionDeploy(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastBastionDeployTime && (t - this._lastBastionDeployTime) < 500) return null;
    this._lastBastionDeployTime = t;
    const regRes = this.playRegistrySound('units', 'BastionLock', 'deploy', {
      category: 'combat_heavy',
      ...opts
    });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playSample('impactPlate_heavy_000.ogg', {
      volume: 0.45,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'combat_heavy',
      ...opts
    });
  }

  // Sul Processing Pillar: Plasma sphere / spear launch & discharge.
  // 400ms Loop gebacken (assets/sound/baked/processing_pillar.m4a),
  // loopt für die Dauer der aktiven Angriffsanimation (2.0s Default) oder bis Stop.
  playProcessingPillar(opts = {}) {
    if (this.isMuted) return;
    const durationMs = opts.durationMs ?? 2000;
    this.stopProcessingPillar(true);
    const id = this.playSample('baked/processing_pillar.m4a', {
      volume: 0.7,
      loop: true,
      durationMs,
      randomPitch: true,
      pitchRange: 0.08,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_infantry',
      ...opts
    });
    this._processingPillarSoundId = id;
    return id;
  }

  stopProcessingPillar(immediate = false) {
    if (this._processingPillarSoundId != null) {
      const sample = this._getSample('baked/processing_pillar.m4a');
      if (sample) {
        try {
          if (immediate) {
            sample.stop(this._processingPillarSoundId);
          } else {
            const curVol = (typeof sample.volume === 'function' ? sample.volume(this._processingPillarSoundId) : null) || 0.7;
            if (typeof sample.fade === 'function') {
              sample.fade(curVol, 0, 80, this._processingPillarSoundId);
              setTimeout(() => {
                try { sample.stop(this._processingPillarSoundId); } catch {}
              }, 90);
            } else {
              sample.stop(this._processingPillarSoundId);
            }
          }
        } catch {}
      }
      this._processingPillarSoundId = null;
    }
  }

  // Sul Monitor Lock: Subterranean emergence rumble
  playMonitorLockEruption(opts = {}) {
    if (this.isMuted) return;
    this.playSample('lowFrequency_explosion_001.ogg', {
      volume: 0.45,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'combat_heavy',
      ...opts
    });
  }

  // Sul Monitor Lock: Instant crush collision impact
  playMonitorLockCrush(opts = {}) {
    if (this.isMuted) return;
    this.playSample('explosionCrunch_003.ogg', {
      volume: 0.5,
      randomPitch: true,
      pitchRange: 0.1,
      category: 'combat_heavy',
      ...opts
    });
  }

  // Sul Monitor Lock: Snapping serpentine mandible strike
  playMonitorLockSnap(opts = {}) {
    if (this.isMuted) return;
    this.playSample('more/motions_and_transitions/swish_1.m4a', {
      volume: 0.35,
      randomPitch: true,
      pitchRange: 0.12,
      category: 'combat_heavy',
      ...opts
    });
  }

  // Sul Enforcer Vulture: Bio-rocket ball ejection (90° vertical launch)
  playVultureBallLaunch(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastVultureLaunchTime && (t - this._lastVultureLaunchTime) < 60) return null;
    this._lastVultureLaunchTime = t;

    const regAttack = this.registry?.units?.EnforcerVulture?.attack;
    if (regAttack && Array.isArray(regAttack.files) && regAttack.files.length > 0) {
      const file = this._pickFromPool(regAttack.files, 'units.EnforcerVulture.attack');
      if (file) {
        return this.playSample(file, {
          volume: regAttack.volume ?? opts.volume ?? 0.35,
          randomPitch: regAttack.randomPitch ?? true,
          pitchRange: regAttack.pitchRange ?? 0.12,
          category: 'combat_infantry',
          ...opts
        });
      }
    }

    return this.playSample('more/motions_and_transitions/swish_1.m4a', {
      volume: opts.volume ?? 0.22,
      randomPitch: true,
      pitchRange: 0.12,
      category: 'combat_infantry',
      ...opts
    });
  }

  // Sul Enforcer Vulture: Mid-air rocket interception clash
  playVultureIntercept(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastVultureInterceptTime && (t - this._lastVultureInterceptTime) < 100) return null;
    this._lastVultureInterceptTime = t;
    return this.playSample('more/explosions/explosion_4.m4a', {
      volume: opts.volume ?? 0.5,
      randomPitch: true,
      pitchRange: 0.15,
      category: 'combat_infantry',
      ...opts
    });
  }

  // Sul Looting Vulture: Solar apex energy beam pulse
  playLootingApexBeam(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastLootingBeamTime && (t - this._lastLootingBeamTime) < 80) return null;
    this._lastLootingBeamTime = t;

    const regAttack = this.registry?.units?.LootingVulture?.attack;
    if (regAttack && Array.isArray(regAttack.files) && regAttack.files.length > 0) {
      const file = this._pickFromPool(regAttack.files, 'units.LootingVulture.attack');
      if (file) {
        return this.playSample(file, {
          volume: regAttack.volume ?? opts.volume ?? 0.5,
          randomPitch: regAttack.randomPitch ?? true,
          pitchRange: regAttack.pitchRange ?? 0.08,
          category: 'combat_infantry',
          ...opts
        });
      }
    }

    return this.playSample('laserLarge_000.ogg', {
      volume: opts.volume ?? 0.38,
      randomPitch: true,
      pitchRange: 0.08,
      category: 'combat_infantry',
      ...opts
    });
  }

  // Sul Looting Vulture: Midas stone/gold transmutation wave
  playMidasTransmute(opts = {}) {
    if (this.isMuted) return null;
    const regTransmute = this.registry?.units?.LootingVulture?.transmute;
    if (regTransmute && Array.isArray(regTransmute.files) && regTransmute.files.length > 0) {
      const file = this._pickFromPool(regTransmute.files, 'units.LootingVulture.transmute');
      if (file) {
        return this.playSample(file, {
          volume: regTransmute.volume ?? opts.volume ?? 0.65,
          randomPitch: regTransmute.randomPitch ?? false,
          category: 'special',
          ...opts
        });
      }
    }

    return this.playSample('even more sounds/Hero_Kill_001.m4a', {
      volume: opts.volume ?? 0.75,
      randomPitch: false,
      category: 'special',
      ...opts
    });
  }

  // Golden Statue: Heavy metallic crash onto ground
  playStatueMetallicCrash(opts = {}) {
    if (this.isMuted) return null;
    const regCrash = this.registry?.units?.LootingVulture?.statueCrash;
    if (regCrash && Array.isArray(regCrash.files) && regCrash.files.length > 0) {
      const file = this._pickFromPool(regCrash.files, 'units.LootingVulture.statueCrash');
      if (file) {
        return this.playSample(file, {
          volume: regCrash.volume ?? opts.volume ?? 0.95,
          randomPitch: regCrash.randomPitch ?? false,
          category: 'effects',
          ...opts
        });
      }
    }

    return this.playSample('impactMining_004.ogg', {
      volume: opts.volume ?? 0.95,
      randomPitch: false,
      category: 'effects',
      ...opts
    });
  }

  // Parasite Vulture: Visceral biological overtake
  playParasiteOvertake(opts = {}) {
    if (this.isMuted) return null;
    const regOvertake = this.registry?.units?.ParasiteVulture?.overtake;
    if (regOvertake && Array.isArray(regOvertake.files) && regOvertake.files.length > 0) {
      const file = this._pickFromPool(regOvertake.files, 'units.ParasiteVulture.overtake');
      if (file) {
        return this.playSample(file, {
          volume: regOvertake.volume ?? opts.volume ?? 0.75,
          randomPitch: regOvertake.randomPitch ?? true,
          category: 'special',
          ...opts
        });
      }
    }
    return this.playSample('even more sounds/80-CC0-creature-SFX/monster_07.ogg', {
      volume: opts.volume ?? 0.75,
      randomPitch: true,
      category: 'special',
      ...opts
    });
  }

  // Deflected / Ricochet: click_001.ogg mit 0.5 Volume (User-Vorgabe 2026-09)
  playRicochet(opts = {}) {
    return this.playDeflected(opts);
  }

  playDeflected(opts = {}) {
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastRicochetTime && (t - this._lastRicochetTime) < 40) return null;
    this._lastRicochetTime = t;

    const isHumanShooter = !!(
      opts.isHumanShooter ||
      opts.shooterRace === 'Human' ||
      opts.attackerRace === 'Human' ||
      opts.faction === 'Human' ||
      opts.race === 'Human' ||
      opts.attacker?.race === 'Human' ||
      opts.attacker?.cardData?.race === 'Human' ||
      opts.attacker?.faction === 'Human' ||
      opts.attacker?.cardData?.faction === 'Human' ||
      opts.sourceUnit?.race === 'Human' ||
      opts.sourceUnit?.cardData?.race === 'Human'
    );

    const bulletCooldownMs = 1200;
    const canPlayBullet = isHumanShooter && (!this._lastBulletDeflectTime || (t - this._lastBulletDeflectTime) >= bulletCooldownMs);

    let chosen;
    if (canPlayBullet) {
      this._lastBulletDeflectTime = t;
      const bulletVariants = ['more/weapons/bullet_2.m4a', 'more/weapons/bullet_3.m4a'];
      chosen = bulletVariants[Math.floor(Math.random() * bulletVariants.length)];
    } else {
      chosen = 'click_001.ogg';
    }

    return this.playSample(chosen, {
      volume: 0.5,
      randomPitch: true,
      category: 'combat_infantry',
      ...opts
    });
  }

  // File-based seit Batch 2 (docs/SOUND_BATCH_PLAN.md section 4.2/1.1).
  // Runde 1 (rocket_01.ogg) hatte nicht überzeugt, Runde 2 (thrusterFire_
  // 001.ogg) wurde genehmigt, dann durch zwei größenabhängige Varianten aus
  // dem "more sound"-Nachschub ersetzt - direkter Nutzer-Fund: 'heavy' für
  // Mehrfach-Raketenwerfer (Orchestra), 'normal' für einzelne Raketen
  playRocketLaunch(size = 'normal', opts = {}) {
    if (typeof size === 'object' && size !== null) {
      opts = size;
      size = opts.size || 'normal';
    }
    if (this.isMuted) return null;
    const t = performance.now();
    if (this._lastRocketLaunchTime && (t - this._lastRocketLaunchTime) < 50) return null;
    this._lastRocketLaunchTime = t;

    const unitKey = opts.unitType || (size === 'heavy' ? 'Orchestra' : 'Rocketship');
    const { volume: callerVolume, ...restOpts } = opts;

    const regAttack = this.registry?.units?.[unitKey]?.rocket_launch || this.registry?.units?.[unitKey]?.attack;
    if (regAttack && Array.isArray(regAttack.files) && regAttack.files.length > 0) {
      const file = this._pickFromPool(regAttack.files, `units.${unitKey}.rocket_launch`);
      if (file) {
        return this.playSample(file, {
          volume: regAttack.volume ?? callerVolume ?? (size === 'heavy' ? 0.35 : 0.45),
          randomPitch: regAttack.randomPitch ?? true,
          pitchRange: regAttack.pitchRange ?? 0.06,
          randomVolume: true,
          volumeRange: 0.08,
          category: 'combat_heavy',
          ...restOpts
        });
      }
    }

    return this.playSample(size === 'heavy' ? 'more/sci_fi/jet_pack_pass_1.m4a' : 'more/motions_and_transitions/air_reverse_burst_3.m4a', {
      volume: callerVolume ?? (size === 'heavy' ? 0.35 : 0.45),
      randomPitch: true,
      pitchRange: 0.06,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_heavy',
      ...restOpts
    });
  }

  // Direkter Nutzer-Fund ("perfekt als Schuss-Geräusch explizit für
  // Shade"): Shade/HyperShade teilten sich bisher playGauss() mit Marine &
  // Co. (js/engine/unit.js) - dieser eigene, charakteristischere Sound
  // (gedämpfter Schuss) grenzt die Stealth-Einheit akustisch ab.
  playSuppressedShot(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastSuppressedShotTime && (t - this._lastSuppressedShotTime) < 45) return;
    this._lastSuppressedShotTime = t;
    this.playSample('more/weapons/gun_silencer.m4a', {
      randomPitch: true,
      pitchRange: 0.06,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_infantry',
      ...opts
    });
  }

  // File-based seit Batch 2 (docs/SOUND_BATCH_PLAN.md section 4.2).
  playPlasma(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastPlasmaTime && (t - this._lastPlasmaTime) < 45) return;
    this._lastPlasmaTime = t;
    this.playSample('forceField_000.ogg', {
      category: 'combat_infantry',
      ...opts
    });
  }

  // File-based seit Batch 2 (docs/SOUND_BATCH_PLAN.md section 4.2/1.1) -
  // jetzt gebacken als sauberes 250ms One-Shot-Sample mit Micro-Fadeout
  // (assets/sound/baked/tesla_zap.m4a, kein Laufzeit-Timeout mehr nötig).
  playTesla(opts = {}) {
    if (this.isMuted) return;
    const t = performance.now();
    if (this._lastTeslaTime && (t - this._lastTeslaTime) < 45) return;
    this._lastTeslaTime = t;
    this.playSample('baked/tesla_zap.m4a', { volume: 1.5, randomPitch: true,
      pitchRange: 0.08,
      randomVolume: true,
      volumeRange: 0.08,
      category: 'combat_infantry',
      ...opts });
  }

  _getNoiseBuffer(dur = 2.0) {
    if (!this.ctx) return null;
    const reqSamples = Math.ceil(this.ctx.sampleRate * dur);
    if (!this._cachedNoiseBuffer || this._cachedNoiseBuffer.length < reqSamples) {
      const buffer = this.ctx.createBuffer(1, reqSamples, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < reqSamples; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      this._cachedNoiseBuffer = buffer;
    }
    return this._cachedNoiseBuffer;
  }

  // 'small'/'medium'/'large'/'heavy'/'massive' - alle file-based seit Batch
  // 2 (docs/SOUND_BATCH_PLAN.md section 4.2). 'massive' (der Nuke-Moment)
  // brauchte 2 Runden: Runde 1 abgelehnt ("muss sich deutlich größer/
  // dramatischer fühlen als heavy"), Runde 2 genehmigt lowFrequency_
  // explosion_000.ogg **plus** Layering über playLayered() - der zweite,
  // leisere Layer ist bewusst dieselbe Datei wie 'heavy' (lowFrequency_
  // explosion_001.ogg): die Überlappung selbst macht den Unterschied
  // hörbar, unabhängig davon, welche Einzeldatei je gewählt wurde.
  playExplosion(size = 'medium', opts = {}) {
    if (typeof size === 'object' && size !== null) {
      opts = size;
      size = opts.size || 'medium';
    }
    if (this.isMuted) return;
    if (size === 'massive') {
      this.playSample('lowFrequency_explosion_000.ogg', {
        volume: 1.5,
        randomPitch: true,
        pitchRange: 0.04,
        category: 'combat_heavy',
        ...opts
      });
      return;
    }
    const isSmallOrMed = (size === 'small' || size === 'medium');
    this.playSample(EXPLOSION_FILES[size] || EXPLOSION_FILES.medium, {
      randomPitch: true,
      pitchRange: isSmallOrMed ? 0.08 : 0.04,
      randomVolume: true,
      volumeRange: isSmallOrMed ? 0.10 : 0.06,
      category: size === 'small' ? 'combat_infantry' : 'combat_heavy',
      ...opts
    });
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3).
  playCardPlay(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'card_play', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('card-place-2.ogg', { category: 'ui', ...opts });
  }

  // Feuert laut Nutzer aktuell nur im Zuge des "Consider"-Spells, nicht bei
  // jedem generischen Discard (Batch 3, docs/SOUND_BATCH_PLAN.md section 4.3).
  playDiscard(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'card_discard', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('card-shove-1.ogg', { category: 'ui', ...opts });
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3) -
  // gebacken als präziser One-Shot (assets/sound/baked/nuke_alarm.m4a).
  // 3x-Wiederholung im 400ms-Abstand aus der Synthese-Version übernommen.
  playNukeAlarm(opts = {}) {
    if (this.isMuted) return null;
    this.triggerCombatDucking(1200, 0.5);
    const res = this.playRegistrySound('spells', 'spell_nuke_alarm', 'fire', opts);
    if (res !== null) return res;
    for (let i = 0; i < 3; i++) {
      setTimeout(() => this.playSample('baked/nuke_alarm.m4a', { category: 'special', ...opts }), i * 400);
    }
  }

  // Sul Bio-Plasma Phase 0 Hush. File-based seit Batch 3 (docs/
  // SOUND_BATCH_PLAN.md section 4.3) - gebacken als präziser One-Shot
  // (assets/sound/baked/bioplasma_hush.m4a).
  playBioPlasmaHush(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'bioplasma_hush', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('baked/bioplasma_hush.m4a', { volume: 1.3, category: 'special', ...opts });
  }

  // Sul Bio-Plasma Phase 2 Incision. File-based seit Batch 3 (docs/
  // SOUND_BATCH_PLAN.md section 4.3).
  playBioPlasmaIncision(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'bioplasma_incision', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('explosionCrunch_002.ogg', { category: 'special', ...opts });
  }

  // Sul Precision Strike (Überflug & Schattenschwingen)
  playPrecisionStrikeFlyby(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_precision_strike_flyby', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/sci_fi/whoosh_down_flutter.m4a', { volume: 0.85, category: 'special', ...opts });
  }

  // Sul Precision Strike (Sporen-Bombe Fall)
  playPrecisionStrikeDrop(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_precision_strike_drop', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/sci_fi/liquid_drops_fx_01.m4a', { volume: 0.75, category: 'special', ...opts });
  }

  // Sul Precision Strike (Einschlag & Bio-Detonation)
  playPrecisionStrikeImpact(opts = {}) {
    if (this.isMuted) return null;
    this.triggerCombatDucking?.(800, 0.4);
    const res = this.playRegistrySound('spells', 'spell_precision_strike_impact', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('explosionCrunch_004.ogg', { volume: 0.95, category: 'combat_heavy', ...opts });
  }

  // File-based seit Batch 3 Runde 2 (docs/SOUND_BATCH_PLAN.md section 4.3) -
  // Runde 1 (card-slide-1.ogg/card-fan-1.ogg/scroll_001.ogg) abgelehnt
  // ("was weicheres an Ton notwendig").
  playCardDraw(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'card_draw', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('bookFlip1.ogg', { category: 'ui', ...opts });
  }

  // File-based seit Batch 3 Runde 2 (docs/SOUND_BATCH_PLAN.md section 4.3) -
  // gebacken als präziser One-Shot (assets/sound/baked/melt_charge.m4a).
  playMeltCharge(opts = {}) {
    if (this.isMuted) return null;
    this.triggerCombatDucking(500, 0.6);
    const res = this.playRegistrySound('spells', 'spell_melt_charge', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('baked/melt_charge.m4a', { category: 'special', ...opts });
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3). Ersetzt
  // den bisherigen mehrstufigen Impact+Akkord+Sparkle-Synthese-Sound durch
  // eine Datei - playUpgradeChime() unten bleibt ein Alias, bekommt den
  // Ersatz also automatisch mit.
  playMeltFusionImpact(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_melt_impact', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('confirmation_004.ogg', { category: 'special', ...opts });
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3).
  playConcentrationCharge(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_conc_charge', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/sci_fi/filter_sweep_fx_09.m4a', { category: 'special', ...opts });
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3).
  playConcentrationBurst(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_conc_burst', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('explosion_02.ogg', { category: 'special', ...opts });
  }

  playUpgradeChime(opts = {}) {
    this.playMeltFusionImpact(opts);
  }

  // Per-unit spawn sound override, falling back to the race's generic spawn bark.
  playUnitSpawn(card, opts = {}) {
    if (this.isMuted || !card) return null;
    const unitKey = (card.unitType || card.name || '').replace(/\s+/g, '');
    const altKey = card.unitType || card.name;

    // Prevent duplicate triggers from firing within the same burst (e.g. simultaneous UI + engine calls)
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this._lastUnitSpawnTimes ??= new Map();
    if (this._lastUnitSpawnTimes.has(unitKey)) {
      const lastTime = this._lastUnitSpawnTimes.get(unitKey);
      if (now - lastTime < 250 && !opts.force) {
        return null;
      }
    }
    this._lastUnitSpawnTimes.set(unitKey, now);

    // 1. Check sound registry pool first (e.g. Hero_Kill_001.m4a, Hero_Attack_002.m4a)
    const regSpawn = this.registry?.units?.[unitKey]?.spawn || this.registry?.units?.[altKey]?.spawn;
    if (regSpawn && Array.isArray(regSpawn.files) && regSpawn.files.length > 0) {
      const file = this._pickFromPool(regSpawn.files, `units.${unitKey}.spawn`);
      if (file) {
        return this.playSample(file, {
          randomPitch: true,
          pitchRange: 0.05,
          randomVolume: true,
          volumeRange: 0.06,
          category: 'barks',
          volume: regSpawn.volume ?? 1.0,
          ...opts
        });
      }
    }

    // 2. Check legacy UNIT_SPAWN_FILES fallback (e.g. Tesla Walker sword_2.m4a)
    const entry = UNIT_SPAWN_FILES[unitKey] || UNIT_SPAWN_FILES[altKey];
    if (entry) {
      const file = typeof entry === 'object' && entry !== null ? entry.file : entry;
      const defaultVol = typeof entry === 'object' && entry !== null && typeof entry.volume === 'number'
        ? entry.volume
        : 1;
      return this.playSample(file, {
        randomPitch: true,
        pitchRange: 0.05,
        randomVolume: true,
        volumeRange: 0.06,
        category: 'barks',
        volume: defaultVol,
        ...opts
      });
    }

    // 3. Fallback to race spawn bark
    return this.playBark(card.race, 'spawn', opts);
  }

  // File-based per (race, type) combo, Batch 5 (docs/SOUND_BATCH_PLAN.md
  // section 4.5) - only Human/Sul are active races, so only those keys ever
  // get populated. Falls through to the original synthesis below for any
  // combo not yet approved in the review tool, and for the 'ui_click'
  // pseudo-race (packOpeningModal.js/showroomModal.js/slotMachineModal.js
  // abuse this method for a generic UI click, not a real race bark -
  // deliberately left on synthesis, out of scope for this batch).
  playBark(race, type = 'select', opts = {}) {
    if (typeof type === 'object' && type !== null) {
      opts = type;
      type = opts.type || 'select';
    }
    if (this.isMuted || this.sfxVolume <= 0) return;
    const entry = BARK_FILES[race]?.[type];
    if (entry) {
      const file = typeof entry === 'object' && entry !== null ? entry.file : entry;
      const defaultVol = typeof entry === 'object' && entry !== null && typeof entry.volume === 'number'
        ? entry.volume
        : 1;
      this.playSample(file, {
        randomPitch: true,
        pitchRange: 0.05,
        randomVolume: true,
        volumeRange: 0.06,
        category: 'barks',
        volume: defaultVol,
        ...opts
      });
      return;
    }
    if (!this.ctx) return;
    // Short synthesized "bark" - a per-faction timbre (Human: blunt square-wave
    // grunt, Sul: deep guttural throat tones)
    // shaped differently per event so units read as reacting, not just
    // playing one generic blip for everything.
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    if (race === 'Sul') {
      // Sul Swarm: Deep oscillating guttural throat tones (sub-bass 85-110 Hz with 36 Hz AM ring modulation)
      try {
        const carrier = this.ctx.createOscillator();
        carrier.type = 'sawtooth';

        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(type === 'spawn' ? 360 : (type === 'death' ? 220 : 290), t);

        const lfo = this.ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(36, t);

        const lfoGain = this.ctx.createGain();
        lfoGain.gain.setValueAtTime(0.45, t);

        const ampGain = this.ctx.createGain();

        let dur = 0.22;
        let baseFreq = 95;
        if (type === 'spawn') {
          dur = 0.32;
          carrier.frequency.setValueAtTime(75, t);
          carrier.frequency.exponentialRampToValueAtTime(120, t + dur * 0.6);
          carrier.frequency.exponentialRampToValueAtTime(85, t + dur);
        } else if (type === 'death') {
          dur = 0.42;
          carrier.frequency.setValueAtTime(115, t);
          carrier.frequency.exponentialRampToValueAtTime(45, t + dur);
        } else {
          // select
          dur = 0.18;
          carrier.frequency.setValueAtTime(baseFreq, t);
          carrier.frequency.exponentialRampToValueAtTime(baseFreq * 0.88, t + dur);
        }

        ampGain.gain.setValueAtTime(0.001, t);
        ampGain.gain.exponentialRampToValueAtTime(Math.max(0.0001, 0.18 * this.sfxVolume), t + 0.02);
        ampGain.gain.exponentialRampToValueAtTime(0.001, t + dur);

        lfo.connect(lfoGain);
        lfoGain.connect(ampGain.gain);

        carrier.connect(filter);
        filter.connect(ampGain);
        ampGain.connect(this.ctx.destination);

        carrier.start(t);
        lfo.start(t);
        carrier.stop(t + dur + 0.02);
        lfo.stop(t + dur + 0.02);
        return;
      } catch (e) {
        // audio fallback
      }
    }

    try {
      let waveType = 'square';
      let baseFreq = 220;
      osc.type = waveType;

      let dur = 0.09;
      if (type === 'spawn') {
        dur = 0.14;
        osc.frequency.setValueAtTime(baseFreq * 0.75, t);
        osc.frequency.exponentialRampToValueAtTime(baseFreq * 1.3, t + dur);
      } else if (type === 'death') {
        dur = 0.22;
        osc.frequency.setValueAtTime(baseFreq * 1.15, t);
        osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.45, t + dur);
      } else {
        // select - a very short flat acknowledge blip
        osc.frequency.setValueAtTime(baseFreq, t);
      }

      gain.gain.setValueAtTime(0.001, t);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, 0.12 * this.sfxVolume), t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(t);
      osc.stop(t + dur + 0.02);
    } catch (e) {
      // audio fallback
    }
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3).
  playError(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'error', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('error_006.ogg', { category: 'ui', ...opts });
  }

  // Plays for BOTH clients when a Special Edition (e.g. Gold) card is
  // played - see js/commands/commandProcessor.js's _play(). File-based
  // seit Batch 3 Runde 2 (docs/SOUND_BATCH_PLAN.md section 4.3) - Runde 1
  // (twoTone1.ogg/twoTone2.ogg) abgelehnt ("eher wie ein chime").
  playSpecialCardAlert() {
    if (this.isMuted) return;
    this.playSample('confirmation_001.ogg');
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3) - eine
  // Datei pro Taunt-Charakter statt der bisherigen Impact+Ton-Zweistufigkeit.
  playTauntBlip(tauntId = 'hello') {
    if (this.isMuted) return;
    if (tauntId === 'gotchya') {
      this.playSample('retro_laser_02.ogg');
    } else if (tauntId === 'revenge') {
      this.playSample('lowThreeTone.ogg');
    } else {
      this.playSample('beep_02.ogg');
    }
  }

  // File-based seit Batch 3 (docs/SOUND_BATCH_PLAN.md section 4.3) -
  // jetzt gebacken als präziser 2-Sekunden-Stinger mit Fadeout
  // (assets/sound/baked/gold_drop.m4a).
  playGoldDropIn(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'gold_drop_in', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('baked/gold_drop.m4a', { volume: 0.45, category: 'ui', ...opts });
  }

  // Cargo-Bay-Pack-Opening-Audio (js/ui/packOpeningModal.js) - der Name
  // "Crate"/"S-Rank Reward Crate" ist ein Überbleibsel aus dem ursprünglich
  // geplanten Belohnungskisten-Feature (docs/S_RANK_REWARD_CHEST_POC_PLAN.md);
  // das wurde nie ausgeliefert, existiert laut Nutzer nicht mehr - real
  // implementiert ist nur das Booster-Pack-Öffnen, das diese Methoden
  // wiederverwendet (docs/SOUND_BATCH_PLAN.md section 4.3).
  //
  // playCrateDrop() hat aktuell keinen Aufrufer (siehe Abschnitt 3.4).
  // File-based seit Batch 3.
  playCrateDrop(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'crate_drop', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('impactPlate_heavy_001.ogg', { category: 'ui', ...opts });
  }

  // File-based seit Batch 6 (docs/SOUND_BATCH_PLAN.md section 4.6) -
  // gebacken als präziser 740ms-Turbinen-Anlauf mit Micro-Fadeout
  // (assets/sound/baked/crate_charge.m4a).
  playCrateCharge() {
    if (this.isMuted) return;
    this.playSample('baked/crate_charge.m4a');
  }

  // Sub-Bass-Thump bleibt Synthese, "Large explosive noise burst" ist schon
  // seit Batch 2 file-basiert+layered (playExplosion('massive')). Die
  // pneumatische Druckstoß-Schicht am Ende ist seit Batch 3 file-basiert
  // (docs/SOUND_BATCH_PLAN.md section 4.3).
  playCrateBurst() {
    if (this.isMuted || this.sfxVolume <= 0) return;

    // Sub-bass detonation thump (45Hz down to 20Hz) - registry-first
    // (cargo_bay.pack_detonation_thump) so an assigned file replaces the
    // oscillator layer; falls through to the original synthesis otherwise.
    const thumpRes = this.playRegistrySound('cargo_bay', 'pack_detonation_thump', 'fire');
    if (thumpRes === null && this.ctx) {
      try {
        const t = this.ctx.currentTime;
        const subOsc = this.ctx.createOscillator();
        const subGain = this.ctx.createGain();
        subOsc.type = 'sine';
        subOsc.frequency.setValueAtTime(110, t);
        subOsc.frequency.exponentialRampToValueAtTime(24, t + 0.6);
        subGain.gain.setValueAtTime(Math.max(0.0001, 0.6 * this.sfxVolume), t);
        subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
        subOsc.connect(subGain);
        subGain.connect(this.ctx.destination);
        subOsc.start(t);
        subOsc.stop(t + 0.62);
      } catch (e) {
        // audio fallback
      }
    }

    // Large explosive noise burst
    this.playExplosion('massive');

    // High-pressure pneumatic shockwave release
    this.playSample('more/impacts_and_crashes/air_pressure_blast_1.m4a');
  }

  playRewardFanfare() {
    if (!this.ctx || this.isMuted || this.sfxVolume <= 0) return;
    try {
      const t = this.ctx.currentTime;
      // Layered celestial triad arpeggio + sub-drone
      const drone = this.ctx.createOscillator();
      const droneGain = this.ctx.createGain();
      drone.type = 'triangle';
      drone.frequency.setValueAtTime(130.81, t); // C3
      droneGain.gain.setValueAtTime(Math.max(0.0001, 0.22 * this.sfxVolume), t);
      droneGain.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
      drone.connect(droneGain);
      droneGain.connect(this.ctx.destination);
      drone.start(t);
      drone.stop(t + 1.22);

      const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51];
      notes.forEach((freq, idx) => {
        const noteTime = t + idx * 0.11;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, noteTime);
        const isLast = idx === notes.length - 1;
        const noteDur = isLast ? 0.9 : 0.28;
        gain.gain.setValueAtTime(0.001, noteTime);
        gain.gain.linearRampToValueAtTime(Math.max(0.0001, 0.25 * this.sfxVolume), noteTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, noteTime + noteDur);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(noteTime);
        osc.stop(noteTime + noteDur + 0.05);
      });
    } catch (e) {
      // audio fallback
    }
  }

  // ---------------------------------------------------------------------
  // Semantic UI & Match Flow Methods (Audio-Pipeline 2.0)
  // Kapselt direkte Dateinamen hinter semantischen Methoden, damit UI-Dateien
  // keine rohen Audio-Dateipfade kennen müssen.
  // ---------------------------------------------------------------------
  playUiClick(opts = {}) {
    if (this.isMuted || this.sfxVolume <= 0) return null;
    const res = this.playRegistrySound('ui', 'click_light', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('click4.ogg', { randomPitch: true, pitchRange: 0.06, category: 'ui', ...opts });
  }

  playModalOpen(opts = {}) {
    if (this.isMuted || this.sfxVolume <= 0) return null;
    const res = this.playRegistrySound('ui', 'modal_open', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('maximize_003.ogg', { category: 'ui', ...opts });
  }

  playModalClose(opts = {}) {
    if (this.isMuted || this.sfxVolume <= 0) return null;
    const res = this.playRegistrySound('ui', 'modal_close', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('minimize_003.ogg', { category: 'ui', ...opts });
  }

  playOnboardingStep(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'onboarding_step', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('confirmation_003.ogg', { category: 'ui', ...opts });
  }

  playOnboardingFinish(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'onboarding_finish', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('confirmation_004.ogg', { category: 'ui', ...opts });
  }

  playRoundStart(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('announcer', 'round_start', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('begin.ogg', { category: 'ui', ...opts });
  }

  playPreMatchHype(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('announcer', 'pre_match_hype', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('prepare_yourself.ogg', { category: 'ui', ...opts });
  }

  playMatchResult(isVictory, isSRank = false, opts = {}) {
    if (this.isMuted) return;
    this.stopCombatSounds();
    this.stopAmbience();
    this.playRegistrySound('announcer', 'match_end_stinger', 'fire', opts) || this.playSample('bong_001.ogg', opts);
    if (isVictory) {
      if (isSRank) {
        const sRankPlayed = this.playRegistrySound('announcer', 'victory_s_rank', 'fire', opts);
        if (!sRankPlayed) this.playSample('flawless_victory.ogg', opts);
      } else {
        const victoryPlayed = this.playRegistrySound('announcer', 'victory', 'fire', opts);
        if (!victoryPlayed) this.playSample('confirmation_001.ogg', opts);
      }
    } else {
      const defeatPlayed = this.playRegistrySound('announcer', 'defeat', 'fire', opts);
      if (!defeatPlayed) this.playSample('lowDown.ogg', opts);
    }
  }

  playBattlefieldAmbience(mapId = 'human') {
    this.stopMusic();
    this.playAmbience('more/sci_fi/drone_low_pulse.m4a');
    this.updateWeather(mapId);
  }

  playHangarAmbience() {
    this.playAmbience('more/sci_fi/drone_machine.m4a');
  }

  // ---------------------------------------------------------------------
  // Base Warfare & Siege Methods (Batch 07)
  // ---------------------------------------------------------------------
  playBaseCannon(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('buildings', 'base_cannon_human', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/weapons/gun_rifle_shot_1.m4a', {
      volume: 0.63, // 30% leiser auf Nutzer-Feedback (war 0.9)
      randomPitch: true,
      pitchRange: 0.04,
      randomVolume: true,
      volumeRange: 0.06,
      category: 'combat_heavy',
      ...opts
    }) || this.playExplosion('medium', opts);
  }

  playBaseEnergy(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('buildings', 'base_energy_sul', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/motions_and_transitions/crunch_pump_fx.m4a', {
      volume: 0.2,
      randomPitch: true,
      pitchRange: 0.05,
      category: 'combat_heavy',
      ...opts
    }) || this.playBark('Sul', 'attack', opts);
  }

  playBaseDestruction(opts = {}) {
    if (this.isMuted) return null;
    this.triggerCombatDucking(1000, 0.4);
    const res = this.playRegistrySound('buildings', 'base_destruction', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/explosions/explosion_3.m4a', {
      category: 'combat_heavy',
      ...opts
    }) || this.playExplosion('massive', opts);
  }

  playBaseUnderAttack(opts = {}) {
    if (this.isMuted) return null;
    const now = performance.now();
    if (this._lastBaseAlarm && now - this._lastBaseAlarm < 10000) return null;
    this._lastBaseAlarm = now;
    this.triggerCombatDucking(600, 0.6);
    const res = this.playRegistrySound('buildings', 'base_under_attack', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('beep_01.ogg', {
      volume: 0.85,
      category: 'special',
      ...opts
    });
  }

  playModuleDockSlam(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('buildings', 'module_dock_slam', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/mechtech/air_valve_blast.m4a', {
      volume: 0.8, // 20% leiser auf Nutzer-Feedback
      category: 'combat_heavy',
      ...opts
    }) || this.playExplosion('small', opts);
  }

  // ---------------------------------------------------------------------
  // Special Weapons & Spell Methods (Batch 08)
  // ---------------------------------------------------------------------
  // Wheelie Bomber: Wasteland Dirt-Bike Engine Rev & Acceleration on Drive-Off
  playWheelieEngine(volume = 0.7, opts = {}) {
    if (typeof volume === 'object' && volume !== null) {
      opts = volume;
      volume = opts.volume ?? 0.7;
    }
    if (this.isMuted) return null;
    this.stopWheelieEngine();
    this._wheelieDriveSample = this._getSample('baked/wheelie_drive.m4a');
    const soundId = this.playSample('baked/wheelie_drive.m4a', {
      volume,
      randomPitch: true,
      pitchRange: 0.06,
      category: 'special',
      ...opts
    });
    this._wheelieSoundId = soundId;
    return soundId;
  }

  stopWheelieEngine(soundId = null) {
    const targetId = soundId != null ? soundId : this._wheelieSoundId;
    const sample = this._wheelieDriveSample || this._getSample('baked/wheelie_drive.m4a');
    if (sample) {
      try {
        if (targetId != null) {
          sample.stop(targetId);
        } else {
          sample.stop();
        }
      } catch {}
    }
    if (soundId == null || soundId === this._wheelieSoundId) {
      this._wheelieSoundId = null;
      this._wheelieDriveSample = null;
    }
  }

  // Flamethrower (Devil Patrol): 2-Phasen-Sound (Start-Kick + Sustained Loop)
  // 1. Beim ersten Schuss: flame_thrower_start.m4a (Zündung/Whoosh) + Loop-Start
  // 2. Während des Feuerns (alle 0.5s): flame_thrower_loop.m4a läuft nahtlos
  // 3. Watchdog (650ms): Fadet den Loop nach 650ms ohne Schuss automatisch sanft aus.
  playFlamethrower(volume = 0.7, opts = {}) {
    if (typeof volume === 'object' && volume !== null) {
      opts = volume;
      volume = opts.volume ?? 0.7;
    }
    if (this.isMuted) return;
    this._resumeContextIfNeeded();

    const pan = opts.pan ?? (opts.x !== undefined && opts.x !== null ? this.panFromX(opts.x) : null);

    if (!this._flamethrowerActive) {
      this._flamethrowerActive = true;
      // Start-Zündung (one-shot, mit dezentem randomPitch)
      this.playSample('more/weapons/flame_thrower_start.m4a', {
        volume: volume * 0.9,
        randomPitch: true,
        pitchRange: 0.05,
        category: 'combat_infantry',
        pan,
        ...opts
      });

      // Sustained Loop
      if (typeof window !== 'undefined' && window.Howl) {
        if (!this._flamethrowerHowl) {
          this._flamethrowerHowl = new window.Howl({
            src: ['assets/sound/more/weapons/flame_thrower_loop.m4a'],
            loop: true,
            volume: Math.max(0, Math.min(1, volume * this.sfxVolume * 0.8))
          });
        } else {
          this._flamethrowerHowl.volume(Math.max(0, Math.min(1, volume * this.sfxVolume * 0.8)));
        }
        if (pan !== null && typeof this._flamethrowerHowl.stereo === 'function') {
          this._flamethrowerHowl.stereo(pan);
        }
        if (!this._flamethrowerHowl.playing()) {
          this._flamethrowerHowl.play();
        }
      }
    } else {
      if (this._flamethrowerHowl && typeof window !== 'undefined' && window.Howl) {
        this._flamethrowerHowl.volume(Math.max(0, Math.min(1, volume * this.sfxVolume * 0.8)));
        if (pan !== null && typeof this._flamethrowerHowl.stereo === 'function') {
          this._flamethrowerHowl.stereo(pan);
        }
      }
    }

    // Watchdog Heartbeat: verlängert den Loop um 650ms. Hört Devil Patrol auf zu schießen, fadet der Loop aus.
    if (this._flamethrowerWatchdog) {
      clearTimeout(this._flamethrowerWatchdog);
    }
    this._flamethrowerWatchdog = setTimeout(() => {
      this.stopFlamethrower();
    }, 650);
  }

  stopFlamethrower(immediate = false) {
    if (this._flamethrowerWatchdog) {
      clearTimeout(this._flamethrowerWatchdog);
      this._flamethrowerWatchdog = null;
    }
    this._flamethrowerActive = false;
    if (this._flamethrowerHowl && this._flamethrowerHowl.playing()) {
      if (immediate) {
        this._flamethrowerHowl.stop();
      } else {
        const curVol = this._flamethrowerHowl.volume();
        this._flamethrowerHowl.fade(curVol, 0, 140);
        setTimeout(() => {
          if (!this._flamethrowerActive) {
            this._flamethrowerHowl?.stop();
          }
        }, 150);
      }
    }
  }

  // Stoppt alle laufenden Kampf-Loops und aktiven Stimmen (z. B. beim Match-Ende oder Menü-Rückkehr)
  stopCombatSounds() {
    this.stopFlamethrower?.(true);
    this.stopProcessingPillar?.(true);
    this.stopA9PiranhaMotorLoop?.();
    this.stopWheelieEngine?.();
    this.stopAntiShadeBreath?.();
    if (this._activeVoices && this._activeVoices.length > 0) {
      const combatVoices = this._activeVoices.filter(v => v.category !== 'ui' && v.category !== 'music');
      for (const entry of combatVoices) {
        try { entry.sample.stop(entry.id); } catch {}
      }
      this._activeVoices = this._activeVoices.filter(v => v.category === 'ui' || v.category === 'music');
    }
  }

  // Anti Shade: Globaler Schattenschleifen-Atem („Darth Vader Breath“)
  playAntiShadeBreath(opts = {}) {
    if (this.isMuted) return null;
    if (this._antiShadeBreathActive) return this._antiShadeBreathSoundId;

    const eventDef = this.registry?.units?.AntiShade?.breath;
    const files = (eventDef && Array.isArray(eventDef.files) && eventDef.files.length > 0)
      ? eventDef.files
      : ['even more sounds/80-CC0-creature-SFX/breath.ogg'];
    const file = this._pickFromPool ? this._pickFromPool(files, 'AntiShade.breath') : files[0];
    const baseVol = eventDef?.volume ?? 0.5;

    this._resumeContextIfNeeded?.();
    this._antiShadeSample = this._getSample(file);
    const soundId = this.playSample(file, {
      volume: baseVol,
      loop: true,
      category: 'special',
      ...opts
    });
    this._antiShadeBreathActive = true;
    this._antiShadeBreathFile = file;
    this._antiShadeBreathSoundId = soundId;
    return soundId;
  }

  stopAntiShadeBreath() {
    if (!this._antiShadeBreathActive) return;
    const file = this._antiShadeBreathFile || 'even more sounds/80-CC0-creature-SFX/breath.ogg';
    const sample = this._antiShadeSample || this._getSample(file);
    if (sample) {
      try {
        if (this._antiShadeBreathSoundId != null) {
          sample.stop(this._antiShadeBreathSoundId);
        } else {
          sample.stop();
        }
      } catch {}
    }
    this._antiShadeBreathActive = false;
    this._antiShadeBreathSoundId = null;
    this._antiShadeBreathFile = null;
    this._antiShadeSample = null;
  }

  playSporeLob(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'ApostateLoaf', 'attack', opts);
    if (res !== null) return res;
    return this.playSample('more/sci_fi/liquid_drops_fx_01.m4a', {
      volume: 0.4,
      randomPitch: true,
      pitchRange: 0.08,
      randomVolume: true,
      volumeRange: 0.10,
      category: 'combat_infantry',
      ...opts
    }) || this.playExplosion('small', opts);
  }

  playSpellStun(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_stun', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('phaseJump1.ogg', { category: 'special', ...opts });
  }

  playSpellBuff(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_buff', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('powerUp8.ogg', { category: 'special', ...opts });
  }

  playSpellHeal(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_heal', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/motions_and_transitions/bell_cascade_1.m4a', { category: 'special', ...opts });
  }

  playSpellClone(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_clone', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('baked/gold_drop.m4a', { category: 'special', ...opts });
  }

  playSpellFodder(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_fodder', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('cloth1.ogg', { volume: 0.75, category: 'special', ...opts });
  }

  playMentalExtraction(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_mental_extraction', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('more/motions_and_transitions/low_frequency_flutter_fx_03.m4a', { volume: 0.8, category: 'special', ...opts });
  }

  playForcedOverstimulation(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_forced_overstimulation', 'fire', opts);
    if (res !== null) return res;
    return this.playSpellBuff(opts);
  }

  playExperimentalMetamorphosis(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_experimental_metamorphosis', 'fire', opts);
    if (res !== null) return res;
    return this.playSpellBuff(opts);
  }

  playRetreatProtocolSignal(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spells', 'spell_retreat_protocol', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('even more sounds/PSE Game Audio Sound Pack/DSGNBram_Energy Deep Space Growling_PSE_COL_zuwtQ.m4a', { volume: 0.85, ...opts });
  }

  // ---------------------------------------------------------------------
  // Special Combat & Unit Weapons (Refinements 2026-09)
  // ---------------------------------------------------------------------
  playHyperTesla(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'HyperTeslaWalker', 'hyperTesla', { category: 'combat_heavy', ...opts })
      || this.playRegistrySound('units', 'HyperTeslaWalker', 'attack', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playSample('baked/hyper_tesla.m4a', {
      volume: 0.8,
      randomPitch: true,
      pitchRange: 0.05,
      category: 'combat_heavy',
      ...opts
    }) || this.playLaser?.(opts);
  }

  playPanzerCannon(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'Panzer', 'attack', { category: 'combat_heavy', ...opts })
      ?? this.playRegistrySound('units', 'SiegeTank', 'attack', { category: 'combat_heavy', ...opts })
      ?? this.playRegistrySound('units', 'HyperPanzer', 'attack', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playSample('more/weapons/gun_rifle_shot_1.m4a', {
      volume: 0.7,
      randomPitch: true,
      pitchRange: 0.04,
      randomVolume: true,
      volumeRange: 0.06,
      category: 'combat_heavy',
      ...opts
    });
  }

  playHyperPanzerCrush(opts = {}) {
    if (this.isMuted) return;
    this.playSample('explosionCrunch_003.ogg', {
      volume: 0.65,
      randomPitch: true,
      pitchRange: 0.12,
      category: 'combat_heavy',
      ...opts
    });
  }

  playHyperDevilShot(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'HyperDevilPatrol', 'attack', { category: 'combat_infantry', ...opts });
    if (res !== null) return res;
    return this.playSample('more/weapons/gun_hand_shot_3.m4a', {
      volume: 0.4,
      randomPitch: true,
      pitchRange: 0.05,
      category: 'combat_infantry',
      ...opts
    });
  }

  playHarvestingTether(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'HarvestingPillar', 'attack', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playSample('more/sci_fi/filter_sweep_fx_04.m4a', {
      volume: 0.06, // 60% leiser auf Nutzer-Feedback (war 0.15)
      throttleMs: 150,
      rate: 0.85 + Math.random() * 0.04, // nach unten gepitcht auf Nutzer-Feedback (war randomPitch um 1.0)
      category: 'combat_heavy',
      ...opts
    });
  }

  playSalvageHorn(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'TheSalvageCompany', 'spawn', { category: 'barks', ...opts });
    if (res !== null) return res;
    return this.playSample('more/transportation/horn_fire_engine.m4a', {
      volume: 0.7,
      category: 'barks',
      ...opts
    });
  }

  playStickmanAttack(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'Stickman', 'attack', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playPanzerCannon(opts);
  }

  playStickmanDeath(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'Stickman', 'death', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playSample('even more sounds/Voices_Sound_Effect_Library/Mp3/Robot/Robot2_12.mp3', { volume: 0.8, ...opts })
      || this.playUnitDeathMachinery(opts);
  }

  playPulpAttack(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'Pulp', 'attack', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playExplosion('small', opts);
  }

  playHyperPanzerRockets(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'HyperPanzer', 'rockets', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playRocketLaunch ? this.playRocketLaunch('normal', { volume: 0.85, ...opts }) : this.playExplosion('small', opts);
  }

  playHotHandPunch(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('HotHand', 'attack', { category: 'combat_infantry', ...opts }) : null;
    if (res !== null && res !== undefined) return res;
    const regRes = this.playRegistrySound('units', 'HotHand', 'attack', { category: 'combat_infantry', ...opts });
    if (regRes !== null) return regRes;
    return this.playSample('more/foley/body_hit_04.m4a', { volume: 0.85, category: 'combat_infantry', ...opts });
  }

  playHotHandSpawn(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('HotHand', 'spawn', opts) : null;
    if (res !== null && res !== undefined) return res;
    const regRes = this.playRegistrySound('units', 'HotHand', 'spawn', { category: 'barks', ...opts });
    if (regRes !== null) return regRes;
    return this.playSample('even more sounds/PSE Game Audio Sound Pack/VOXEfrt_Female Exclaim02_PSE_SL-FL_ZQ93X.m4a', { category: 'barks', volume: 0.95, ...opts });
  }

  playHotHandDeath(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('HotHand', 'death', opts) : null;
    if (res !== null && res !== undefined) return res;
    const regRes = this.playRegistrySound('units', 'HotHand', 'death', { category: 'barks', ...opts });
    if (regRes !== null) return regRes;
    return this.playSample('more/people/gasp_1.m4a', { category: 'barks', volume: 0.85, ...opts });
  }

  playHotHandSlide(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'HotHand', 'slide', { category: 'movement', ...opts });
    if (res !== null) return res;
    return null;
  }

  playPinkBeretRocketLaunch(opts = {}) {
    if (this.isMuted) return null;
    const regRes = this.playRegistrySound('units', 'PinkBeret', 'rocket_launch', { category: 'combat_heavy', ...opts });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playRocketLaunch('heavy', { unitType: 'PinkBeret', ...opts });
  }

  playPinkBeretRocketImpact(opts = {}) {
    if (this.isMuted) return null;
    const regRes = this.playRegistrySound('units', 'PinkBeret', 'rocket_impact', { category: 'combat_heavy', ...opts });
    if (regRes !== null && regRes !== undefined) return regRes;
    return this.playSample('baked/pink_beret_rocket_impact.m4a', { category: 'combat_heavy', volume: 0.9, ...opts });
  }

  playPinkBeretPistol(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('PinkBeret', 'attack', opts) : null;
    if (res !== null && res !== undefined) return res;
    return this.playRegistrySound('units', 'PinkBeret', 'attack', { category: 'combat_infantry', ...opts });
  }

  playPinkBeretKiss(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'PinkBeret', 'kiss', { category: 'barks', ...opts });
    if (res !== null && res !== undefined) return res;
    return this.playPinkBeretDeath(opts);
  }

  playPinkBeretHeartPop(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('units', 'PinkBeret', 'heart_pop', { category: 'combat_infantry', ...opts });
    if (res !== null && res !== undefined) return res;
    return this.playBalloonPop(opts);
  }

  playPinkBeretSpawn(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('PinkBeret', 'spawn', opts) : null;
    if (res !== null && res !== undefined) return res;
    return this.playRegistrySound('units', 'PinkBeret', 'spawn', { category: 'barks', ...opts });
  }

  playPinkBeretDeath(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playUnitSound ? this.playUnitSound('PinkBeret', 'death', opts) : null;
    if (res !== null && res !== undefined) return res;
    const regRes = this.playRegistrySound('units', 'PinkBeret', 'death', { category: 'barks', ...opts });
    if (regRes !== null) return regRes;
    return this.playSample('more/people/kiss_1.m4a', { category: 'barks', volume: 0.95, ...opts });
  }

  playKiss(opts = {}) {
    return this.playPinkBeretKiss(opts);
  }

  playBalloonPop(opts = {}) {
    if (this.isMuted) return null;
    return this.playSample('more/misc/balloon_pop.m4a', { category: 'combat_infantry', volume: 0.85, ...opts });
  }

  playHackerHarpoon(opts = {}) {
    if (this.isMuted) return null;
    return this.playSample('more/sci_fi/hacker_harpoon.m4a', { category: 'combat_infantry', volume: 0.85, ...opts });
  }

  playHackerRevealChime(opts = {}) {
    if (this.isMuted) return null;
    return this.playSample('more/sci_fi/mid_frequency_flutter_fx_01.m4a', { category: 'combat_infantry', volume: 0.9, ...opts });
  }

  playHackerTranscendence(opts = {}) {
    if (this.isMuted) return null;
    return this.playSample('more/sci_fi/hacker_transcendence.m4a', { category: 'barks', volume: 0.95, ...opts });
  }

  playHackerDeath(opts = {}) {
    if (this.isMuted) return null;
    return this.playSample('more/sci_fi/hacker_death.m4a', { category: 'barks', volume: 0.9, ...opts });
  }

  // ---------------------------------------------------------------------
  // Spawns & Summoned Entities (Drones, Capsules, Cocoon, Parachutes)
  // ---------------------------------------------------------------------
  playDroneLaunch(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'suicide_drone', 'launch', { category: 'combat_infantry', ...opts });
    if (res !== null) return res;
    return this.playLaser ? this.playLaser('small', opts) : this.playSample('laserSmall_001.ogg', opts);
  }

  playDroneDetonation(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'suicide_drone', 'detonation', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playExplosion ? this.playExplosion('small', opts) : this.playSample('explosion_01.ogg', opts);
  }

  playCapsuleLaunch(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'invasion_capsule', 'launch', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playExplosion ? this.playExplosion('small', opts) : this.playSample('more/explosions/explosion_1.m4a', opts);
  }

  playCapsuleImpact(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'invasion_capsule', 'impact', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playExplosion ? this.playExplosion('heavy', opts) : this.playSample('more/explosions/explosion_3.m4a', opts);
  }

  playParachuteOpen(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'parachute_drop', 'open', { category: 'special', ...opts });
    if (res !== null) return res;
    return this.playSample('cloth1.ogg', { volume: 0.65, ...opts });
  }

  playParachuteTouchdown(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'parachute_drop', 'touchdown', { category: 'barks', ...opts });
    if (res !== null) return res;
    return this.playSample('more/foley/body_hit_02.m4a', { volume: 0.6, ...opts });
  }

  playCocoonForm(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'sul_cocoon', 'form', { category: 'special', ...opts });
    if (res !== null) return res;
    return this.playSample('even more sounds/80-CC0-RPG-SFX/creature_slime_01.ogg', { volume: 0.85, ...opts });
  }

  playCocoonHatch(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'sul_cocoon', 'hatch', { category: 'barks', ...opts });
    if (res !== null) return res;
    return this.playBark ? this.playBark('Sul', 'spawn', opts) : this.playSample('baked/sul_spawn.m4a', opts);
  }

  playSulSpawn(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'sul_symbiosis', 'spawn', { category: 'barks', ...opts });
    if (res !== null) return res;
    return this.playSample('baked/sul_spawn.m4a', { volume: 0.85, ...opts });
  }

  playStickmanWreck(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('spawns', 'stickman_obstacle', 'collapse', { category: 'combat_heavy', ...opts });
    if (res !== null) return res;
    return this.playSample('explosionCrunch_000.ogg', { volume: 0.85, ...opts });
  }

  // ---------------------------------------------------------------------
  // Navigation & UI Sounds (Refinements 2026-09)
  // ---------------------------------------------------------------------
  playDeckHover(opts = {}) {
    if (this.isMuted) return;
    this._lastDeckHoverIs5 = !this._lastDeckHoverIs5;
    const file = this._lastDeckHoverIs5 ? 'click5.ogg' : 'click4.ogg';
    this.playSample(file, {
      volume: 0.1,
      randomPitch: false,
      throttleMs: 30,
      category: 'ui',
      ...opts
    });
  }

  playUiTabSwitch(opts = {}) {
    if (this.isMuted) return;
    this.playSample('mouseclick1.ogg', {
      volume: 0.6,
      category: 'ui',
      ...opts
    });
  }

  playNetworkAlert(opts = {}) {
    if (this.isMuted) return;
    this.playSample('beep_01.ogg', {
      volume: 0.8,
      category: 'ui',
      ...opts
    });
  }

  playNetworkResumed(opts = {}) {
    if (this.isMuted) return;
    this.playSample('phaseJump1.ogg', {
      volume: 0.7,
      category: 'ui',
      ...opts
    });
  }

  playArtworkSelect(opts = {}) {
    if (this.isMuted) return;
    const n = Math.floor(Math.random() * 9) + 1;
    this.playSample(`switch${n}.ogg`, {
      volume: 0.4,
      randomPitch: true,
      pitchRange: 0.05,
      category: 'ui',
      ...opts
    });
  }

  // ---------------------------------------------------------------------
  // Unit Destruction Sounds (Refinements 2026-09)
  // ---------------------------------------------------------------------
  playUnitDeathInfantry(opts = {}) {
    if (this.isMuted) return;
    const samples = [
      'more/foley/bodyfall_on_dirt_3.m4a',
      'more/impacts_and_crashes/bullet_impact_body_2.m4a',
      'more/foley/bodyfall_on_dirt_1.m4a'
    ];
    const file = samples[Math.floor(Math.random() * samples.length)];
    this.playSample(file, {
      volume: 0.1,
      randomPitch: true,
      pitchRange: 0.06,
      category: 'combat_infantry',
      ...opts
    });
  }

  playUnitDeathMachinery(opts = {}) {
    if (this.isMuted) return;
    const num = 3 + Math.floor(Math.random() * 4); // 3..6
    const file = `more/explosions/explosion_${num}.m4a`;
    this.playSample(file, {
      volume: 0.1,
      randomPitch: true,
      pitchRange: 0.06,
      category: 'combat_heavy',
      ...opts
    });
  }

  // ---------------------------------------------------------------------
  // Casino & Minigame Methods (Batch 09 - Approved & Wired)
  // ---------------------------------------------------------------------
  playCasinoChipBet(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'casino_chip_bet', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('chip-lay-1.ogg', { randomPitch: true, pitchRange: 0.06, category: 'ui', ...opts });
  }

  playCasinoWin(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'casino_chips_win', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('handleCoins.ogg', { category: 'ui', ...opts });
  }

  playCasinoSpin(opts = {}) {
    if (this.isMuted) return null;
    this.stopCasinoSpin();
    const regSpin = this.registry?.ui?.casino_reel_spin;
    const file = (regSpin && Array.isArray(regSpin.files) && regSpin.files[0]) || 'scroll_002.ogg';
    this._casinoSpinSample = this._getSample(file);
    this._casinoSpinSoundId = this.playSample(file, {
      loop: true,
      rate: 0.75,
      durationMs: 2900,
      category: 'ui',
      volume: regSpin?.volume ?? 0.7,
      ...opts
    });
    return this._casinoSpinSoundId;
  }

  stopCasinoSpin() {
    if (this._casinoSpinSoundId != null) {
      const sample = this._casinoSpinSample || this._getSample('scroll_002.ogg');
      if (sample) {
        try { sample.stop(this._casinoSpinSoundId); } catch {}
      }
      this._casinoSpinSoundId = null;
      this._casinoSpinSample = null;
    }
  }

  playSlotHoloReveal(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'slot_holo_reveal_new', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('powerUp10.ogg', { category: 'ui', volume: 0.8, ...opts });
  }

  playSlotRefund(opts = {}) {
    if (this.isMuted) return null;
    const res = this.playRegistrySound('ui', 'slot_refund_hexagons', 'fire', opts);
    if (res !== null) return res;
    return this.playSample('handleCoins2.ogg', { category: 'ui', volume: 0.85, ...opts });
  }

  // ---------------------------------------------------------------------
  // Dynamic Weather & Atmospheric Ambience
  // ---------------------------------------------------------------------
  _ensureWeatherLoop(key, file) {
    this._weatherLoops ??= {};
    if (!this._weatherLoops[key]) {
      if (typeof window !== 'undefined' && window.Howl) {
        this._weatherLoops[key] = new CrossfadeLoop(`assets/sound/${file}`, { volume: 0, fadeMs: 1500 });
      } else {
        let _vol = 0;
        let _playing = false;
        this._weatherLoops[key] = {
          src: [`assets/sound/${file}`],
          volume: (v) => { if (v !== undefined) _vol = v; return _vol; },
          playing: () => _playing,
          play: () => { _playing = true; },
          pause: () => { _playing = false; },
          stop: () => { _playing = false; _vol = 0; }
        };
      }
    }
    return this._weatherLoops[key];
  }

  _setWeatherLoopVolume(key, file, targetVol) {
    if (this.isMuted) targetVol = 0;
    const finalVol = Math.max(0, Math.min(1, targetVol * this.sfxVolume));
    // updateWeather() lists the loops of every map with volume 0 for the inactive ones. Creating a
    // loop just to keep it silent would fetch and decode ~250 MB of ambience nobody hears.
    if (finalVol <= 0.001 && !this._weatherLoops?.[key]) return;
    const loop = this._ensureWeatherLoop(key, file);
    if (!loop) return;

    // Audio-Dirty-Checking (v3 §3 P2 #1):
    // Avoid redundant Web Audio volume parameter setter invocations when difference is imperceptible
    if (!this._weatherLastVol) this._weatherLastVol = new Map();
    const last = this._weatherLastVol.get(key) ?? -1;
    if (Math.abs(last - finalVol) < 0.005) return;
    this._weatherLastVol.set(key, finalVol);

    if (finalVol > 0.001) {
      if (typeof loop.playing === 'function' && !loop.playing()) {
        loop.play();
      }
      if (typeof loop.volume === 'function') {
        loop.volume(finalVol);
      }
    } else {
      if (typeof loop.playing === 'function' && loop.playing()) {
        loop.volume(0);
        loop.pause();
      }
    }
  }

  updateWeather(mapId = 'human', nowSec = null) {
    if (this.isMuted) {
      this.stopWeather();
      return;
    }
    const t = nowSec !== null ? nowSec : (performance.now() * 0.001);

    // Rate-limit weather updates to 10 Hz (every 100ms) unless map changed (v3 §3 P2 #1)
    if (this._lastWeatherUpdateT && (t - this._lastWeatherUpdateT) < 0.10 && this._lastWeatherMapId === mapId) {
      return;
    }
    this._lastWeatherUpdateT = t;
    this._lastWeatherMapId = mapId;

    if (mapId === 'human' || !mapId) {
      // 60s cycle (weather.js)
      const cyclePos = t % 60;
      // Rain: 6s to 32s (26s duration, 2s ramp)
      let rainIntensity = 0;
      if (cyclePos >= 6 && cyclePos < 32) {
        const rAge = cyclePos - 6;
        rainIntensity = Math.min(1.0, Math.min(rAge / 2.0, (26 - rAge) / 2.0));
      }
      // Wind: 38s to 52s (14s duration, 2s ramp)
      let windIntensity = 0;
      if (cyclePos >= 38 && cyclePos < 52) {
        const wAge = cyclePos - 38;
        windIntensity = Math.min(1.0, Math.min(wAge / 2.0, (14 - wAge) / 2.0));
      }

      this._setWeatherLoopVolume('city_rain', 'more/ambience/rain_4.m4a', rainIntensity * 0.3);
      this._setWeatherLoopVolume('city_wind', 'more/ambience/wind_3.m4a', windIntensity * 0.1);
      // Zusätzliches, durchgehendes City-Ambience-Loop auf Nutzer-Feedback (analog zum
      // durchgehenden voidglass_wind-Loop unten, keine Zyklus-Modulation).
      this._setWeatherLoopVolume('city_2', 'more/ambience/city_2.m4a', 0.2);
      this._setWeatherLoopVolume('sul_wind', 'more/ambience/wind_4.m4a', 0);
      this._setWeatherLoopVolume('swamp_fog', 'more/ambience/wind_haunted.m4a', 0);
      this._setWeatherLoopVolume('swamp_country_night', 'more/ambience/country_night_02.m4a', 0);
      this._setWeatherLoopVolume('swamp_river', 'more/ambience/forest_stream_river_water_01.m4a', 0);
      this._setWeatherLoopVolume('swamp_forest_night', 'more/ambience/forest_night.m4a', 0);
      this._setWeatherLoopVolume('voidglass_wind', 'more/ambience/wind_2.m4a', 0);
      this._setWeatherLoopVolume('stratos_wind', 'more/ambience/wind_3.m4a', 0);
    } else if (mapId === 'sul') {
      // 54s cycle (backgroundSul.js)
      const cyclePos = t % 54;
      // Wind: 20s to 36s (16s duration, 2.5s ramp)
      let sulWindIntensity = 0;
      if (cyclePos >= 20 && cyclePos < 36) {
        const age = cyclePos - 20;
        sulWindIntensity = Math.min(1.0, Math.min(age / 2.5, (16 - age) / 2.5));
      }

      this._setWeatherLoopVolume('sul_wind', 'more/ambience/wind_4.m4a', sulWindIntensity * 0.3);
      this._setWeatherLoopVolume('city_rain', 'more/ambience/rain_4.m4a', 0);
      this._setWeatherLoopVolume('city_wind', 'more/ambience/wind_3.m4a', 0);
      this._setWeatherLoopVolume('city_2', 'more/ambience/city_2.m4a', 0);
      this._setWeatherLoopVolume('swamp_fog', 'more/ambience/wind_haunted.m4a', 0);
      this._setWeatherLoopVolume('swamp_country_night', 'more/ambience/country_night_02.m4a', 0);
      this._setWeatherLoopVolume('swamp_river', 'more/ambience/forest_stream_river_water_01.m4a', 0);
      this._setWeatherLoopVolume('swamp_forest_night', 'more/ambience/forest_night.m4a', 0);
      this._setWeatherLoopVolume('voidglass_wind', 'more/ambience/wind_2.m4a', 0);
      this._setWeatherLoopVolume('stratos_wind', 'more/ambience/wind_3.m4a', 0);
    } else if (mapId === 'swamp') {
      // 70s cycle (backgroundSwamp.js)
      if (this._swampWeatherOffset === undefined) this._swampWeatherOffset = 0;
      const cyclePos = (t + this._swampWeatherOffset) % 70;
      let fogIntensity = 0;
      if (cyclePos < 18) {
        if (cyclePos < 4) fogIntensity = cyclePos / 4;
        else if (cyclePos < 14) fogIntensity = 1.0;
        else fogIntensity = 1.0 - (cyclePos - 14) / 4;
      }

      this._setWeatherLoopVolume('swamp_fog', 'more/ambience/wind_haunted.m4a', fogIntensity * 0.1);
      // Zusätzliche durchgehende Swamp-Ambience-Loops auf Nutzer-Feedback (analog zum
      // durchgehenden voidglass_wind-Loop unten, keine Zyklus-Modulation).
      this._setWeatherLoopVolume('swamp_country_night', 'more/ambience/country_night_02.m4a', 0.3);
      this._setWeatherLoopVolume('swamp_river', 'more/ambience/forest_stream_river_water_01.m4a', 0.1);
      this._setWeatherLoopVolume('swamp_forest_night', 'more/ambience/forest_night.m4a', 0.3);
      this._setWeatherLoopVolume('city_rain', 'more/ambience/rain_4.m4a', 0);
      this._setWeatherLoopVolume('city_wind', 'more/ambience/wind_3.m4a', 0);
      this._setWeatherLoopVolume('city_2', 'more/ambience/city_2.m4a', 0);
      this._setWeatherLoopVolume('sul_wind', 'more/ambience/wind_4.m4a', 0);
      this._setWeatherLoopVolume('voidglass_wind', 'more/ambience/wind_2.m4a', 0);
      this._setWeatherLoopVolume('stratos_wind', 'more/ambience/wind_3.m4a', 0);
    } else if (mapId === 'voidglass') {
      // Continuous loop of Wind 2 at volume 0.15
      this._setWeatherLoopVolume('voidglass_wind', 'more/ambience/wind_2.m4a', 0.15);
      this._setWeatherLoopVolume('city_rain', 'more/ambience/rain_4.m4a', 0);
      this._setWeatherLoopVolume('city_wind', 'more/ambience/wind_3.m4a', 0);
      this._setWeatherLoopVolume('city_2', 'more/ambience/city_2.m4a', 0);
      this._setWeatherLoopVolume('sul_wind', 'more/ambience/wind_4.m4a', 0);
      this._setWeatherLoopVolume('swamp_fog', 'more/ambience/wind_haunted.m4a', 0);
      this._setWeatherLoopVolume('swamp_country_night', 'more/ambience/country_night_02.m4a', 0);
      this._setWeatherLoopVolume('swamp_river', 'more/ambience/forest_stream_river_water_01.m4a', 0);
      this._setWeatherLoopVolume('swamp_forest_night', 'more/ambience/forest_night.m4a', 0);
      this._setWeatherLoopVolume('stratos_wind', 'more/ambience/wind_3.m4a', 0);
    } else if (mapId === 'stratos') {
      // Continuous loop of Stratospheric Wind at volume 0.18
      this._setWeatherLoopVolume('stratos_wind', 'more/ambience/wind_3.m4a', 0.18);
      this._setWeatherLoopVolume('city_rain', 'more/ambience/rain_4.m4a', 0);
      this._setWeatherLoopVolume('city_wind', 'more/ambience/wind_3.m4a', 0);
      this._setWeatherLoopVolume('city_2', 'more/ambience/city_2.m4a', 0);
      this._setWeatherLoopVolume('sul_wind', 'more/ambience/wind_4.m4a', 0);
      this._setWeatherLoopVolume('swamp_fog', 'more/ambience/wind_haunted.m4a', 0);
      this._setWeatherLoopVolume('swamp_country_night', 'more/ambience/country_night_02.m4a', 0);
      this._setWeatherLoopVolume('swamp_river', 'more/ambience/forest_stream_river_water_01.m4a', 0);
      this._setWeatherLoopVolume('swamp_forest_night', 'more/ambience/forest_night.m4a', 0);
      this._setWeatherLoopVolume('voidglass_wind', 'more/ambience/wind_2.m4a', 0);
    } else {
      this.stopWeather();
    }
  }

  stopWeather() {
    if (this._weatherLastVol) this._weatherLastVol.clear();
    this._lastWeatherUpdateT = 0;
    if (this._weatherLoops) {
      for (const key in this._weatherLoops) {
        try {
          this._weatherLoops[key].stop();
          this._weatherLoops[key].unload?.();
        } catch {}
      }
      this._weatherLoops = {};
    }
  }
}

